import React, { useState, useEffect, useRef } from 'react';
import './WorldDbUploader.css';
import { database, auth, functions as regionalFunctions } from '../firebaseConfig';
import { ref, set, push, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, getDocs, setDoc, runTransaction, writeBatch, query, where, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firestore } from '../firebaseConfig';
import { buildSearchTerms } from './searchUtils';
import { lookupBlacklistForEmails } from '../shared/blacklistUtils';
import { computeDomainDedup, extractDomain, isBusinessDomain } from '../shared/dedupNormalize';
import {
  normalizeBusinessNameStrong,
  computeNamePhonetic,
  nameSimilarity,
} from '../shared/fuzzyNameMatch';
import useCustomFields from '../hooks/useCustomFields';

const WorldDbUploader = ({ onClose, onReload, userId: propUserId }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mappedColumns, setMappedColumns] = useState({});
  const [fileName, setFileName] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  // 🔒 TEAM SUPPORT: userId wird jetzt als Prop übergeben (für Team-Mitglieder ist das die Owner's ID)
  const [userId, setUserId] = useState(propUserId || null);
  const [userEmail, setUserEmail] = useState(null);
  const [step, setStep] = useState(1); // 1: File selection, 2: Column mapping, 3: Confirmation
  const [delimiter, setDelimiter] = useState(';'); // Default delimiter
  const [importing, setImporting] = useState(false); // Loading state for the import process
  const [importOption, setImportOption] = useState('all'); // 'all' oder 'email_only'
  const [dupCheckComplete, setDupCheckComplete] = useState(false); // Zeigt an, ob die Duplikat-Prüfung abgeschlossen ist
  const [duplicateEmails, setDuplicateEmails] = useState([]); // Liste von doppelten E-Mails
  const [duplicateCount, setDuplicateCount] = useState(0); // Anzahl der doppelten E-Mails
  const [duplicateBusinessNames, setDuplicateBusinessNames] = useState([]); // Liste von doppelten Business-Namen
  const [duplicateBusinessCount, setDuplicateBusinessCount] = useState(0); // Anzahl der doppelten Business-Namen
  // Phase E-Fix (2026-08, Import-Guard): pro Duplikat-Email/Business die
  // *existierenden* Kontakte, mit denen kollidiert wird. Ohne diese Info
  // sieht der User nur "E-Mail X existiert schon" — jetzt sieht er
  // "E-Mail X → gehört zu Firma Y" und kann informiert entscheiden.
  const [duplicateEmailMatches, setDuplicateEmailMatches] = useState({}); // { normalizedEmail: [{id, businessName}] }
  const [duplicateBusinessMatches, setDuplicateBusinessMatches] = useState({}); // { normalizedName: [{id, email, businessName}] }
  const [duplicateDomains, setDuplicateDomains] = useState([]); // Liste von doppelten Firmen-Domains (nur neue Docs)
  const [duplicateDomainCount, setDuplicateDomainCount] = useState(0); // Anzahl der doppelten Firmen-Domains
  const [enrichmentCount, setEnrichmentCount] = useState(0); // Anzahl bestehender Einträge, die angereichert werden
  const [internalDuplicateCount, setInternalDuplicateCount] = useState(0); // Anzahl der internen Duplikate
  // Opt-in Zusatzprüfungen innerhalb der CSV (Default aus = bisheriges Verhalten).
  // Die Standard-Prüfung vergleicht nur E-Mail bzw. Business-Name; zwei Zeilen
  // derselben Firma mit abweichender Schreibweise rutschen dadurch durch.
  const [dedupeByWebsite, setDedupeByWebsite] = useState(false);
  const [dedupeByPhone, setDedupeByPhone] = useState(false);
  const [internalWebsiteDuplicateCount, setInternalWebsiteDuplicateCount] = useState(0);
  const [internalPhoneDuplicateCount, setInternalPhoneDuplicateCount] = useState(0);
  const [showDuplicates, setShowDuplicates] = useState(false); // UI-Status für Anzeige doppelter E-Mails
  const [showDuplicateBusinesses, setShowDuplicateBusinesses] = useState(false); // UI-Status für Anzeige doppelter Business-Namen
  const [showDuplicateDomains, setShowDuplicateDomains] = useState(false); // UI-Status für Anzeige doppelter Domains
  // Additiver Fuzzy-Layer: "mögliche ähnliche Treffer" (gleiche Firma, Name nicht
  // exakt gleich), die NICHT bereits über E-Mail/Business/Domain erkannt wurden.
  // Default: alle werden importiert (Verhalten unverändert); der User markiert
  // optional einzelne als Duplikat zum Überspringen.
  const [similarGroups, setSimilarGroups] = useState([]); // [{ rowIndex, row, candidates: [{id,data,score}] }]
  const [similarDecisions, setSimilarDecisions] = useState({}); // { [rowIndex]: 'import' | 'skip' }
  const [showSimilarMatches, setShowSimilarMatches] = useState(true); // UI-Status (default offen, da Aktion nötig)
  const [similarTruncated, setSimilarTruncated] = useState(false); // true, wenn mehr Gruppen existierten als angezeigt
  // Anreicherungs-Plan (Phase 1b): Map<existingDocId, { existingData, rows: [] }>.
  // Wird beim Dup-Check befüllt und in confirmAndImport abgearbeitet. Bewusst ein
  // Ref (kein State), damit confirmAndImport immer den frischen Plan sieht.
  const enrichmentPlanRef = useRef(new Map());
  const [blacklistedEmails, setBlacklistedEmails] = useState([]); // Liste von blockierten E-Mails
  const [blacklistedCount, setBlacklistedCount] = useState(0); // Anzahl der blockierten E-Mails
  const [showBlacklisted, setShowBlacklisted] = useState(false); // UI-Status für Anzeige blockierter E-Mails
  const [filteredData, setFilteredData] = useState([]); // Die gefilterten Daten für den Import
  const [isUploading, setIsUploading] = useState(false); // Der tatsächliche Upload-Status
  const [importProgress, setImportProgress] = useState(0);
  const [confirmedRights, setConfirmedRights] = useState(false); // Bestätigung für Rechte an der Kontaktliste
  const [showLegalText, setShowLegalText] = useState(false); // Anzeige für rechtliche Informationen
  const [importTemplate, setImportTemplate] = useState(''); // Per-import default template
  const [importTemplateStages, setImportTemplateStages] = useState({
    follow_up_1: '',
    follow_up_2: '',
    follow_up_3: '',
  }); // Optional per-stage template overrides
  const [showStageTemplates, setShowStageTemplates] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]); // Available custom templates
  // Einmalige Migration: befüllt lead_email_dedup / business_name_dedup auf
  // allen bereits existierenden Docs. Ohne diesen Schritt würde die neue
  // where('...', 'in', [...])-basierte Duplikat-Prüfung alte Docs nicht sehen.
  const [migrationState, setMigrationState] = useState('idle'); // idle | running | done | error
  const [migrationStats, setMigrationStats] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Get user ID and email via Firebase Auth
  // 🔒 TEAM SUPPORT: Wenn userId als Prop übergeben wird, verwende diese (Team-System)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Wenn eine userId als Prop übergeben wurde, verwende diese (für Team-Mitglieder)
        // Andernfalls verwende die eigene userId
        if (!propUserId) {
          setUserId(user.uid);
        }
        setUserEmail(user.email);
      } else {
        setUserId(null);
        setUserEmail(null);
      }
    });
    return () => unsubscribe();
  }, [propUserId]);
  
  // Aktualisiere userId wenn sich propUserId ändert
  useEffect(() => {
    if (propUserId) {
      setUserId(propUserId);
    }
  }, [propUserId]);

  // Load custom templates for optional per-import template assignment
  useEffect(() => {
    if (!userId) return;
    const customRef = ref(database, `World/${userId}/settings/customTopics`);
    const unsub = get(customRef).then((snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([id, t]) => ({
          id,
          topic: t.topic || id
        }));
        setCustomTemplates(list);
      }
    });
  }, [userId]);

  // IP für das Consent-Log kommt aus unserem eigenen Backend, nicht von einem
  // Drittdienst — der Nachweis soll keine zusätzliche Datenübermittlung auslösen.
  const getUserIP = async () => {
    try {
      const callable = httpsCallable(regionalFunctions, 'getClientIp');
      const result = await callable();
      return result?.data?.ip || 'unknown';
    } catch (error) {
      console.error('Error fetching IP address:', error);
      return 'unknown';
    }
  };

  // DSGVO-konforme Speicherung der Consent-Daten
  const saveConsentLog = async (importFileName, entriesCount) => {
    try {
      const userIP = await getUserIP();
      const consentData = {
        timestamp: new Date().toISOString(),
        user_id: userId,
        user_email: userEmail,
        ip_address: userIP,
        file_name: importFileName,
        entries_imported: entriesCount,
        consent_text: 'I confirm that I have all rights to use and upload this contact list and agree to the Terms of Service.',
        terms_url: 'https://www.bonusclub.io/terms',
        user_agent: navigator.userAgent
      };
      
      // Speichere in einer separaten Collection für Consent-Logs
      const consentRef = doc(collection(firestore, `World/${userId}/consent_logs`));
      await setDoc(consentRef, consentData);
      
      console.log('Consent log saved successfully:', consentData);
      return true;
    } catch (error) {
      console.error('Error saving consent log:', error);
      return false;
    }
  };

  const { customFields } = useCustomFields(userId);

  const importFieldGroups = React.useMemo(() => {
    const groups = [];
    if (customFields.length > 0) {
      groups.push({ label: 'Custom Fields', fields: customFields.map((cf) => {
        const slug = cf.key.replace('cf_', '');
        const labelMatchesKey = cf.label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') === slug;
        return [cf.key, labelMatchesKey ? cf.label : `${cf.label} (${slug})`];
      })});
    }
    groups.push(
      { label: 'Basic', fields: [
        ['business_name', 'Business Name'],
        ['lead_description', 'Description (AI Context)'],
        ['lead_email', 'Email'],
        ['lead_telephone', 'Telephone'],
        ['lead_address', 'Address'],
        ['lead_city', 'City'],
        ['lead_postcode', 'Postcode'],
        ['lead_country', 'Country'],
      ]},
      { label: 'Social Media', fields: [
        ['_smart_socials', 'Smart Socials (auto-detect)'],
        ['lead_instagram', 'Instagram'],
        ['lead_linkedin', 'LinkedIn'],
        ['lead_facebook', 'Facebook'],
        ['lead_tiktok', 'TikTok'],
        ['lead_twitter', 'X / Twitter'],
        ['lead_youtube', 'YouTube'],
        ['lead_xing', 'Xing'],
        ['lead_website', 'Website'],
      ]},
      { label: 'Metrics', fields: [
        ['lead_rating', 'Total Rating (#)'],
        ['lead_reviews', 'Total Reviews (#)'],
        ['lead_stars', 'Stars (#)'],
        ['lead_instagram_followers', 'Instagram Followers (#)'],
      ]},
      { label: 'Other', fields: [
        ['lead_link', 'Link'],
        ['lead_image', 'Image'],
        ['lead_mapsurl', 'Google Maps URL'],
      ]},
      { label: 'Decision Maker', fields: [
        ['dm_name', 'Name'],
        ['dm_role', 'Position'],
        ['dm_email', 'Email'],
        ['dm_phone', 'Phone'],
      ]},
    );
    return groups;
  }, [customFields]);

  // Flat map for lookups (auto-mapping, defaults, etc.)
  const availableFields = React.useMemo(() => {
    const flat = {};
    importFieldGroups.forEach(g => g.fields.forEach(([key, label]) => { flat[key] = label; }));
    return flat;
  }, [importFieldGroups]);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Detect the delimiter (comma or semicolon) from the CSV content
  const detectDelimiter = (text) => {
    // Get the first line
    const firstLine = text.split('\n')[0];
    
    // Count occurrences of each delimiter
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const doubleColonCount = (firstLine.match(/;;/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    
    // Prüfe auf doppelte Trennzeichen (;;)
    if (doubleColonCount > 0 && semicolonCount >= doubleColonCount * 2) {
      return ';;';
    }
    
    // Return the most frequent delimiter
    return semicolonCount >= commaCount ? ';' : ',';
  };

  // Parse CSV with the detected delimiter
  const parseCSV = (text, delimiter) => {
    const lines = text.split('\n');
    const rows = [];
    let currentRow = [];
    let currentValue = '';
    let inQuotes = false;
    
    // Spezielle Behandlung für doppelte Trennzeichen
    const isDoubleDelimiter = delimiter === ';;';
    const actualDelimiter = isDoubleDelimiter ? ';' : delimiter;
    
    if (isDoubleDelimiter) {
      console.log("Detected double delimiter (;;), using special parsing mode");
      
      // Bei doppelten Trennzeichen anders parsen
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line) {
          // Spalte aufteilen und leere Spalten filtern
          const columns = line.split(';;')
            .map(col => col.trim())
            .filter((col, index) => index % 2 === 0 || col !== ''); // Filtere leere Spalten jede zweite Spalte
          
          rows.push(columns);
        }
      }
      
      // Nur wenn Daten vorhanden sind
      if (rows.length === 0) {
        return { header: [], rowsData: [] };
      }
      
      const header = rows[0];
      const rowsData = rows.slice(1);
      
      return { header, rowsData };
    }
    
    // Normale Verarbeitung für einfache Trennzeichen
    // Process each character in the file
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1] || '';
      
      // Handle quotes
      if (char === '"') {
        // If next character is also a quote and we're inside quotes, it's an escaped quote
        if (nextChar === '"' && inQuotes) {
          currentValue += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } 
      // Handle delimiters
      else if (char === actualDelimiter && !inQuotes) {
        currentRow.push(currentValue.trim());
        currentValue = '';
      } 
      // Handle new lines
      else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
        // Add the last value of the current row
        currentRow.push(currentValue.trim());
        
        // Only add non-empty rows
        if (currentRow.some(cell => cell !== '')) {
          rows.push([...currentRow]);
        }
        
        // Reset for next row
        currentRow = [];
        currentValue = '';
        
        // Skip the \n if we just processed \r
        if (char === '\r') {
          i++;
        }
      }
      // Normal character
      else {
        currentValue += char;
      }
    }
    
    // Handle the last row if there's any data
    if (currentValue !== '' || currentRow.length > 0) {
      currentRow.push(currentValue.trim());
      if (currentRow.some(cell => cell !== '')) {
        rows.push([...currentRow]);
      }
    }
    
    // Ensure we have data
    if (rows.length === 0) {
      return { header: [], rowsData: [] };
    }
    
    const rawHeader = rows[0];
    const nonEmptyIndices = rawHeader
      .map((col, idx) => (col !== '' ? idx : -1))
      .filter(idx => idx !== -1);
    const header = nonEmptyIndices.map(idx => rawHeader[idx]);
    
    // Filter data rows to match the non-empty header positions,
    // then normalize length to match the filtered header.
    const rowsData = rows.slice(1).map(row => {
      const filtered = nonEmptyIndices.map(idx => (idx < row.length ? row[idx] : ''));
      if (filtered.length < header.length) {
        return [...filtered, ...Array(header.length - filtered.length).fill('')];
      }
      if (filtered.length > header.length) {
        return filtered.slice(0, header.length);
      }
      return filtered;
    });
    
    return { header, rowsData };
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setCsvFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          
          // Detect the delimiter
          const detectedDelimiter = detectDelimiter(text);
          setDelimiter(detectedDelimiter);
          
          // Parse the CSV with the detected delimiter
          const { header, rowsData } = parseCSV(text, detectedDelimiter);
          
          if (header.length === 0) {
            alert('No valid data found in the CSV file.');
            return;
          }
          
          setColumns(header);
          setCsvRows(rowsData);
          
          const suggestedMappings = {};
          const usedTargets = new Set();
          
          const autoRules = [
            { target: 'lead_email', patterns: ['email', 'mail', 'e-mail'] },
            { target: 'business_name', patterns: ['business', 'company', 'firma', 'unternehmen', 'name', 'business_name'] },
            { target: 'lead_description', patterns: ['description', 'beschreibung', 'context', 'notes', 'notizen'] },
            { target: 'lead_telephone', patterns: ['telefon', 'telephone', 'phone', 'tel'] },
            // Social platforms before website so "Instagram URL" matches instagram, not website
            { target: 'lead_instagram', patterns: ['instagram', 'insta', 'ig'] },
            { target: 'lead_linkedin', patterns: ['linkedin'] },
            { target: 'lead_facebook', patterns: ['facebook', 'fb'] },
            { target: 'lead_tiktok', patterns: ['tiktok', 'tik tok'] },
            { target: 'lead_twitter', patterns: ['twitter', 'x.com', 'x / twitter'] },
            { target: 'lead_youtube', patterns: ['youtube', 'yt'] },
            { target: 'lead_xing', patterns: ['xing'] },
            { target: '_smart_socials', patterns: ['social media', 'social medias', 'socials', 'social links', 'social profiles'] },
            { target: 'lead_website', patterns: ['website', 'web', 'url', 'homepage'] },
            { target: 'lead_city', patterns: ['city', 'stadt', 'ort'] },
            { target: 'lead_address', patterns: ['address', 'adresse', 'street', 'strasse'] },
            { target: 'lead_country', patterns: ['country', 'land'] },
            { target: 'lead_postcode', patterns: ['postcode', 'plz', 'zip', 'postal'] },
            ...customFields.map((cf) => ({
              target: cf.key,
              patterns: [cf.label.toLowerCase(), cf.key.replace('cf_', '')]
            })),
          ];
          
          header.forEach((col, idx) => {
            const lowerCol = col.toLowerCase().trim();
            for (const rule of autoRules) {
              if (usedTargets.has(rule.target)) continue;
              if (rule.patterns.some(p => lowerCol === p || lowerCol.includes(p))) {
                suggestedMappings[idx] = rule.target;
                usedTargets.add(rule.target);
                break;
              }
            }
          });
          
          if (Object.keys(suggestedMappings).length > 0) {
            setMappedColumns(suggestedMappings);
          }
          
          setStep(2); // Proceed to column mapping
        } catch (error) {
          console.error('Error parsing CSV file:', error);
          alert('Error parsing CSV file. Please check the format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleMappingChange = (index, value) => {
    setMappedColumns((prev) => {
      const newMapping = { ...prev };
      if (value !== 'lead_description') {
        for (const [otherIndex, mappedValue] of Object.entries(newMapping)) {
          if (otherIndex !== index.toString() && mappedValue === value) {
            newMapping[otherIndex] = '';
          }
        }
      }
      newMapping[index] = value;
      return newMapping;
    });
  };

  useEffect(() => {
    // Auto-mapping is now handled in handleFileChange
  }, [columns]);

  const atLeastOneFieldAssigned = 
    columns.length > 0 && 
    Object.values(mappedColumns).some(value => value && value.trim() !== "") &&
    (Object.values(mappedColumns).includes('lead_email') || Object.values(mappedColumns).includes('business_name'));

  const SOCIAL_PLATFORM_PATTERNS = [
    { field: 'lead_facebook', patterns: ['facebook.com', 'fb.com', 'fb.me'] },
    { field: 'lead_instagram', patterns: ['instagram.com', 'instagr.am'] },
    { field: 'lead_linkedin', patterns: ['linkedin.com'] },
    { field: 'lead_tiktok', patterns: ['tiktok.com'] },
    { field: 'lead_twitter', patterns: ['twitter.com', 'x.com'] },
    { field: 'lead_youtube', patterns: ['youtube.com', 'youtu.be'] },
    { field: 'lead_xing', patterns: ['xing.com'] },
  ];

  const parseSocialLinks = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return {};
    const results = {};
    const urls = rawValue.match(/(?:https?:\/\/|www\.)[^\s,;|]+/gi) || [];

    for (const url of urls) {
      const lower = url.toLowerCase();
      for (const platform of SOCIAL_PLATFORM_PATTERNS) {
        if (platform.patterns.some(p => lower.includes(p))) {
          if (!results[platform.field]) {
            results[platform.field] = url;
          }
          break;
        }
      }
    }
    return results;
  };

  // Parse CSV data into an array of objects based on mapping
  const parseCsvData = () => {
    const mappedFieldsSummary = Object.entries(mappedColumns)
      .filter(([, v]) => v && v.trim())
      .map(([idx, field]) => `col[${idx}]="${columns[idx]}" → ${field}`);
    console.log(`[parseCsvData] Mapping: ${mappedFieldsSummary.join(', ')}`);

    const results = csvRows.map((row, rowIdx) => {
      const rowData = {};
      const descriptionParts = [];
      const dmData = {};
      columns.forEach((col, index) => {
        const fieldKey = mappedColumns[index];
        if (fieldKey && fieldKey.trim() !== "") {
          const rawVal = index < row.length ? row[index] : undefined;
          if (fieldKey === 'lead_description') {
            const val = rawVal ? String(rawVal).trim() : "";
            if (val) descriptionParts.push(val);
          } else if (fieldKey === '_smart_socials') {
            const detected = parseSocialLinks(rawVal);
            Object.entries(detected).forEach(([socialField, socialUrl]) => {
              if (!rowData[socialField]) rowData[socialField] = socialUrl;
            });
          } else if (fieldKey.startsWith('dm_')) {
            const val = rawVal ? String(rawVal).trim() : "";
            if (val) dmData[fieldKey] = val;
          } else {
            rowData[fieldKey] = rawVal ? String(rawVal).trim() : "";
          }
        }
      });
      if (descriptionParts.length > 0) {
        rowData.lead_description = descriptionParts.join(' / ');
      }
      if (dmData.dm_name) {
        const dm = { name: dmData.dm_name };
        if (dmData.dm_role) dm.role = dmData.dm_role;
        if (dmData.dm_email) dm.email = dmData.dm_email;
        if (dmData.dm_phone) dm.phone = dmData.dm_phone;
        rowData.lead_decision_makers = JSON.stringify([dm]);
      }
      if (rowIdx < 3) {
        console.log(`[parseCsvData] Row ${rowIdx}: desc="${rowData.lead_description || ''}", email="${rowData.lead_email || ''}", biz="${rowData.business_name || ''}"`);
      }
      return rowData;
    });

    const withDesc = results.filter(r => r.lead_description);
    console.log(`[parseCsvData] ${withDesc.length}/${results.length} rows have lead_description`);
    return results;
  };

  // Neue Hilfsfunktion zur Validierung von E-Mail-Werten
  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    // Trimmen und in Kleinbuchstaben umwandeln
    const normalized = email.trim().toLowerCase();
    
    // Prüfe auf leere E-Mail-Strings oder Platzhalter
    if (normalized === '' || 
        normalized === '0' || 
        normalized === 'null' || 
        normalized === '-' || 
        normalized === 'undefined') {
      return false;
    }
    
    // Einfache E-Mail-Validierung (enthält @)
    return normalized.includes('@');
  };

  // Die Zusatzprüfungen können nur greifen, wenn die passende Spalte gemappt ist.
  const hasWebsiteColumn = Object.values(mappedColumns).includes('lead_website');
  const hasPhoneColumn = Object.values(mappedColumns).includes('lead_telephone');
  const dedupeByWebsiteActive = dedupeByWebsite && hasWebsiteColumn;
  const dedupeByPhoneActive = dedupeByPhone && hasPhoneColumn;

  // Firmen-Domain ausschliesslich aus dem Website-Feld (kein Fallback auf die
  // E-Mail-Domain, damit die Option genau das tut, was ihr Label verspricht).
  // Free-Mail-Hosts werden verworfen, damit z.B. gmx.de nie zur "Firma" wird.
  const extractWebsiteDomain = (item) => {
    const domain = extractDomain(item?.lead_website);
    return domain && isBusinessDomain(domain) ? domain : '';
  };

  // Telefonnummer auf reine Ziffern in internationaler Schreibweise reduzieren.
  // '+49 152 3388 8889', '0049-15233888889' und '+4915233888889' ergeben denselben
  // Schlüssel. Eine rein nationale Nummer ('0152...') bleibt bewusst ein eigener
  // Schlüssel, weil ohne Länderkontext nicht sicher zugeordnet werden kann.
  const normalizePhoneForComparison = (value) => {
    if (!value || typeof value !== 'string') return '';
    const first = value.split(/[,;/|]/)[0] || '';
    let digits = first.trim().replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) digits = digits.slice(1);
    else if (digits.startsWith('00')) digits = digits.slice(2);
    digits = digits.replace(/\D/g, '');
    // Zu kurze Werte (Durchwahlen, kaputte Zellen) nicht zum Matchen verwenden.
    return digits.length >= 7 ? digits : '';
  };

  // Verbessere die Duplikatsprüfung für E-Mails
  const removeInternalDuplicates = (parsedData) => {
    setInternalDuplicateCount(0);
    setInternalWebsiteDuplicateCount(0);
    setInternalPhoneDuplicateCount(0);
    if (!parsedData || parsedData.length === 0) return parsedData;
    
    console.log(`Checking for duplicates in ${parsedData.length} entries`);
    
    const emailMap = new Map();
    const bizNameMap = new Map();
    const websiteMap = new Map();
    const phoneMap = new Map();
    const uniqueItems = [];
    const duplicates = [];
    let websiteDuplicates = 0;
    let phoneDuplicates = 0;
    
    parsedData.forEach((item, index) => {
      // Opt-in: Website/Telefon werden VOR dem Standard-Check ausgewertet, damit
      // sie auch Zeilen erwischen, die unterschiedliche E-Mails oder abweichend
      // geschriebene Firmennamen haben. Zeilen ohne den jeweiligen Wert bleiben
      // unberührt.
      if (dedupeByWebsiteActive) {
        const domain = extractWebsiteDomain(item);
        if (domain) {
          if (websiteMap.has(domain)) {
            duplicates.push({ type: 'website', value: domain, index });
            websiteDuplicates += 1;
            return;
          }
          websiteMap.set(domain, true);
        }
      }

      if (dedupeByPhoneActive) {
        const phone = normalizePhoneForComparison(item.lead_telephone);
        if (phone) {
          if (phoneMap.has(phone)) {
            duplicates.push({ type: 'phone', value: phone, index });
            phoneDuplicates += 1;
            return;
          }
          phoneMap.set(phone, true);
        }
      }

      if (item.lead_email && isValidEmail(item.lead_email)) {
        const normalizedEmail = normalizeEmailForComparison(item.lead_email);
        if (!emailMap.has(normalizedEmail)) {
          emailMap.set(normalizedEmail, true);
          uniqueItems.push(item);
        } else {
          duplicates.push({ type: 'email', value: normalizedEmail, index });
        }
      } else if (item.business_name && item.business_name.trim()) {
        const normalizedBiz = normalizeBusinessName(item.business_name);
        if (!bizNameMap.has(normalizedBiz)) {
          bizNameMap.set(normalizedBiz, true);
          uniqueItems.push(item);
        } else {
          duplicates.push({ type: 'business', value: normalizedBiz, index });
        }
      } else {
        uniqueItems.push(item);
      }
    });
    
    setInternalDuplicateCount(duplicates.length);
    setInternalWebsiteDuplicateCount(websiteDuplicates);
    setInternalPhoneDuplicateCount(phoneDuplicates);
    if (duplicates.length > 0) {
      console.log(`Found ${duplicates.length} internal duplicates (${websiteDuplicates} by website, ${phoneDuplicates} by phone)`);
    }
    
    return uniqueItems;
  };

  // Duplikat-Prüfung auf E-Mail-Ebene.
  //
  // Skaliert mit der Anzahl NEUER Imports, nicht mit der DB-Größe. Statt die
  // komplette `World/{userId}/db` Collection zu laden und clientseitig zu
  // vergleichen, fragen wir Firestore gezielt: "Welche dieser 30 E-Mails
  // existieren schon?" — in Batches parallelisiert.
  //
  // Voraussetzung: jeder Doc hat das Feld `lead_email_dedup`, befüllt mit
  // derselben Normalisierung wie `normalizeEmailForComparison`. Neue Imports
  // setzen das Feld automatisch, Altdatenbestände wurden per Cloud Function
  // (backfillDedupFields) migriert.
  const checkDuplicateEmails = async (parsedData) => {
    if (!userId) return [];

    try {
      const emailMap = new Map();
      const dataWithEmails = parsedData.filter(item =>
        item.lead_email && isValidEmail(item.lead_email)
      );
      dataWithEmails.forEach(item => {
        const normalizedEmail = normalizeEmailForComparison(item.lead_email);
        if (normalizedEmail) emailMap.set(normalizedEmail, item);
      });

      const uniqueEmails = Array.from(emailMap.keys());
      console.log(`Checking ${uniqueEmails.length} unique emails for duplicates in database`);

      if (uniqueEmails.length === 0) {
        setDuplicateEmails([]);
        setDuplicateCount(0);
        setDuplicateEmailMatches({});
        return [];
      }

      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      // Firestore 'in'-Queries akzeptieren bis zu 30 Werte pro Clause.
      const CHUNK_SIZE = 30;
      const chunks = [];
      for (let i = 0; i < uniqueEmails.length; i += CHUNK_SIZE) {
        chunks.push(uniqueEmails.slice(i, i + CHUNK_SIZE));
      }

      // Chunks parallel abarbeiten — alle Antworten sind klein (max. 30 Docs),
      // also egal wie viele Leads in der DB sind, hier wird nie mehr als
      // O(neuImports) Daten übertragen.
      const chunkResults = await Promise.all(
        chunks.map(batch => {
          const q = query(dbCollectionRef, where('lead_email_dedup', 'in', batch));
          return getDocs(q);
        })
      );

      const duplicatesSet = new Set();
      // Phase E-Fix: pro Email die existierenden Kontakte sammeln, damit
      // der User im Import-UI sieht welchem existierenden Kontakt die
      // Adresse gehört. Ohne diese Info musste er raten oder in der DB
      // suchen — mit HubSpot/Salesforce-Style-UX unbrauchbar.
      const matches = {}; // { normalizedEmail: [{id, businessName}] }
      chunkResults.forEach(snap => {
        snap.forEach(docSnap => {
          const data = docSnap.data() || {};
          const dedup = data.lead_email_dedup;
          if (typeof dedup === 'string' && dedup) {
            duplicatesSet.add(dedup);
            if (!matches[dedup]) matches[dedup] = [];
            matches[dedup].push({
              id: docSnap.id,
              businessName: data.business_name || '(ohne Name)',
            });
          }
        });
      });

      const duplicates = Array.from(duplicatesSet);
      console.log(`Found ${duplicates.length} duplicate emails (scanned ${chunks.length} batch(es))`);

      setDuplicateEmails(duplicates);
      setDuplicateCount(duplicates.length);
      setDuplicateEmailMatches(matches);

      return duplicates;
    } catch (error) {
      console.error('Error checking for duplicate emails:', error);
      return [];
    }
  };

  // Neue Hilfsfunktion zur Normalisierung von Business-Namen
  const normalizeBusinessName = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    // Entferne extra Leerzeichen, Sonderzeichen und konvertiere zu Kleinbuchstaben
    return name.trim()
      .toLowerCase()
      // Entferne alle Sonderzeichen
      .replace(/[^\w\s]/g, '')
      // Ersetze mehrere Leerzeichen durch ein einzelnes Leerzeichen
      .replace(/\s+/g, ' ');
  };

  // Duplikat-Prüfung auf Business-Name-Ebene.
  //
  // Gleiche Strategie wie checkDuplicateEmails: gezielte where('...', 'in', [...])
  // Queries in 30er-Batches, parallelisiert. Vergleichsfeld ist
  // `business_name_dedup` (stark normalisiert: lowercase + Sonderzeichen weg
  // + collapsed whitespace — siehe normalizeBusinessName).
  const checkDuplicateBusinessNames = async (parsedData, emailDuplicates) => {
    if (!userId) return [];

    try {
      const emailDupSet = new Set(emailDuplicates || []);

      // Filter: nur Einträge mit Business-Name, die nicht schon per E-Mail als
      // Duplikat abgefangen wurden. Wir vergleichen beide Seiten normalisiert.
      const businessMap = new Map();
      parsedData.forEach(item => {
        const hasBusiness = item.business_name && item.business_name.trim() !== '';
        if (!hasBusiness) return;

        const notDuplicateByEmail = !item.lead_email ||
          !isValidEmail(item.lead_email) ||
          !emailDupSet.has(normalizeEmailForComparison(item.lead_email));

        if (!notDuplicateByEmail) return;

        const normalizedName = normalizeBusinessName(item.business_name);
        if (normalizedName) businessMap.set(normalizedName, item);
      });

      const uniqueBusinessNames = Array.from(businessMap.keys());
      console.log(`Checking ${uniqueBusinessNames.length} unique business names for duplicates`);

      if (uniqueBusinessNames.length === 0) {
        setDuplicateBusinessNames([]);
        setDuplicateBusinessCount(0);
        setDuplicateBusinessMatches({});
        return [];
      }

      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const CHUNK_SIZE = 30;
      const chunks = [];
      for (let i = 0; i < uniqueBusinessNames.length; i += CHUNK_SIZE) {
        chunks.push(uniqueBusinessNames.slice(i, i + CHUNK_SIZE));
      }

      const chunkResults = await Promise.all(
        chunks.map(batch => {
          const q = query(dbCollectionRef, where('business_name_dedup', 'in', batch));
          return getDocs(q);
        })
      );

      const duplicatesSet = new Set();
      const matches = {}; // { normalizedName: [{id, businessName, email}] }
      chunkResults.forEach(snap => {
        snap.forEach(docSnap => {
          const data = docSnap.data() || {};
          const dedup = data.business_name_dedup;
          if (typeof dedup === 'string' && dedup) {
            duplicatesSet.add(dedup);
            if (!matches[dedup]) matches[dedup] = [];
            matches[dedup].push({
              id: docSnap.id,
              businessName: data.business_name || '(ohne Name)',
              email: (data.lead_email || '').split(',')[0].trim() || '',
            });
          }
        });
      });

      const duplicates = Array.from(duplicatesSet);
      console.log(`Found ${duplicates.length} duplicate business names (scanned ${chunks.length} batch(es))`);

      setDuplicateBusinessNames(duplicates);
      setDuplicateBusinessCount(duplicates.length);
      setDuplicateBusinessMatches(matches);

      return duplicates;
    } catch (error) {
      console.error('Error checking for duplicate business names:', error);
      return [];
    }
  };

  // Duplikat-Prüfung auf Firmen-Domain-Ebene (Phase 1).
  //
  // Gleiche Strategie wie checkDuplicateEmails: gezielte
  // where('lead_domain_dedup', 'in', [...]) Queries in 30er-Batches.
  //
  // WICHTIG / bruchsicher:
  //   - Es werden NUR Docs gefunden, die das Feld `lead_domain_dedup` haben —
  //     also ausschließlich Einträge, die ab jetzt importiert/angereichert
  //     werden. Legacy-Docs ohne Feld matchen NIE, dort bleibt alles wie bisher.
  //   - Nur Zeilen mit einer echten Firmen-Domain nehmen teil (Free-Mail/leer
  //     wird von computeDomainDedup zu '' und ignoriert). Dadurch ist ein
  //     versehentliches Massen-Skip ausgeschlossen.
  const checkDuplicateDomains = async (parsedData) => {
    // Bruchsicheres Default-Ergebnis: leer = nichts wird gematcht/verändert.
    const emptyResult = { duplicates: [], enrichByDomain: new Map(), skipDomains: new Set() };
    if (!userId) return emptyResult;

    try {
      const domainMap = new Map();
      (parsedData || []).forEach(item => {
        const domain = computeDomainDedup(item);
        if (domain) domainMap.set(domain, item);
      });

      const uniqueDomains = Array.from(domainMap.keys());
      console.log(`Checking ${uniqueDomains.length} unique company domains for duplicates`);

      if (uniqueDomains.length === 0) {
        setDuplicateDomains([]);
        setDuplicateDomainCount(0);
        return emptyResult;
      }

      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const CHUNK_SIZE = 30;
      const chunks = [];
      for (let i = 0; i < uniqueDomains.length; i += CHUNK_SIZE) {
        chunks.push(uniqueDomains.slice(i, i + CHUNK_SIZE));
      }

      const chunkResults = await Promise.all(
        chunks.map(batch => {
          const q = query(dbCollectionRef, where('lead_domain_dedup', 'in', batch));
          return getDocs(q);
        })
      );

      // Pro Domain sammeln, WELCHE bestehenden Docs sie haben. Nur bei genau EINEM
      // Treffer reichern wir sicher an; bei mehreren ist die Zuordnung mehrdeutig
      // → dann überspringen wir (wie bisher), statt zu raten.
      const docsByDomain = new Map();
      chunkResults.forEach(snap => {
        snap.forEach(docSnap => {
          const data = docSnap.data();
          const dedup = data.lead_domain_dedup;
          if (typeof dedup !== 'string' || !dedup) return;
          if (!docsByDomain.has(dedup)) docsByDomain.set(dedup, []);
          docsByDomain.get(dedup).push({ id: docSnap.id, data });
        });
      });

      const enrichByDomain = new Map();
      const skipDomains = new Set();
      for (const [domain, docs] of docsByDomain.entries()) {
        if (docs.length === 1) {
          enrichByDomain.set(domain, docs[0]);
        } else {
          skipDomains.add(domain);
        }
      }

      const duplicates = Array.from(docsByDomain.keys());
      console.log(`Found ${duplicates.length} existing company domains (${enrichByDomain.size} enrich, ${skipDomains.size} skip-ambiguous; scanned ${chunks.length} batch(es))`);

      setDuplicateDomains(duplicates);
      setDuplicateDomainCount(duplicates.length);

      return { duplicates, enrichByDomain, skipDomains };
    } catch (error) {
      console.error('Error checking for duplicate domains:', error);
      // Bruchsicher: bei Fehlern KEINE Domains als Duplikat melden, damit der
      // Import niemals wegen dieser neuen Prüfung blockiert/verändert wird.
      setDuplicateDomains([]);
      setDuplicateDomainCount(0);
      return emptyResult;
    }
  };

  // Additive Ähnlichkeits-Prüfung ("mögliche ähnliche Treffer").
  //
  // Findet bestehende Kontakte, deren Firmenname dem zu importierenden Namen
  // ÄHNELT (gleiche Firma, aber Schreibweise/Rechtsform/Wortstellung anders),
  // die aber NICHT bereits über E-Mail/Business-Name/Domain als Duplikat erkannt
  // wurden (wir laufen ausschließlich über die bereits gefilterte Importmenge).
  //
  // BRUCHSICHER / skaliert auf 10k:
  //   - Nur indexierte where('...', 'in', [...])-Queries in 30er-Batches mit
  //     Concurrency-Limit (wie die bestehenden Checks) — keine Full-Scans.
  //   - Findet ausschließlich Docs, die die neuen Felder business_name_strong /
  //     business_name_phonetic besitzen (also ab jetzt importierte/angereicherte).
  //     Altbestand ohne diese Felder matcht NIE → der Schritt wird dort still
  //     übersprungen, nichts am bestehenden Verhalten ändert sich.
  //   - Reines ANZEIGEN: es wird nichts automatisch übersprungen oder gemerged.
  const checkSimilarBusinessNames = async (rowsToImport) => {
    const empty = { groups: [], truncated: false };
    if (!userId) return empty;

    try {
      const MIN_KEY_LEN = 2;             // phonetischer Schlüssel mind. so lang
      const MIN_STRONG_LEN = 3;          // starker Name mind. so lang (Rauschen vermeiden)
      const SIMILARITY_THRESHOLD = 0.86; // nur für phonetik-basierte Kandidaten
      const MAX_CANDIDATES_PER_ROW = 4;  // pro Importzeile max. Vorschläge
      const MAX_GROUPS = 200;            // UI-Cap (Render bleibt schnell)
      const PER_QUERY_LIMIT = 400;       // Sicherheits-Cap pro Chunk-Query
      const CHUNK_SIZE = 30;             // Firestore 'in' max. 30 Werte
      const CONCURRENCY = 8;             // parallele Chunk-Queries

      // 1. Zeilen-Metadaten + eindeutige Lookup-Schlüssel sammeln.
      const rowsMeta = [];
      const strongSet = new Set();
      const phoneticSet = new Set();
      rowsToImport.forEach((row, rowIndex) => {
        const name = (row.business_name || '').trim();
        if (!name) return;
        const strong = normalizeBusinessNameStrong(name);
        if (!strong || strong.length < MIN_STRONG_LEN) return;
        const phonetic = computeNamePhonetic(name);
        rowsMeta.push({ rowIndex, name, strong, phonetic });
        strongSet.add(strong);
        if (phonetic && phonetic.length >= MIN_KEY_LEN) phoneticSet.add(phonetic);
      });

      if (rowsMeta.length === 0) return empty;

      const dbCollectionRef = collection(firestore, `World/${userId}/db`);

      // Hilfsfunktion: where(field,'in',[...]) über alle Werte, Concurrency-limitiert.
      const queryByField = async (field, values) => {
        const arr = Array.from(values);
        const map = new Map(); // wert -> [{ id, data }]
        if (arr.length === 0) return map;

        const chunks = [];
        for (let i = 0; i < arr.length; i += CHUNK_SIZE) {
          chunks.push(arr.slice(i, i + CHUNK_SIZE));
        }

        for (let i = 0; i < chunks.length; i += CONCURRENCY) {
          const slice = chunks.slice(i, i + CONCURRENCY);
          const snaps = await Promise.all(
            slice.map(batch => {
              const q = query(dbCollectionRef, where(field, 'in', batch), limit(PER_QUERY_LIMIT));
              return getDocs(q);
            })
          );
          snaps.forEach(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const v = data[field];
              if (typeof v !== 'string' || !v) return;
              if (!map.has(v)) map.set(v, []);
              map.get(v).push({ id: docSnap.id, data });
            });
          });
        }
        return map;
      };

      const [strongMap, phoneticMap] = await Promise.all([
        queryByField('business_name_strong', strongSet),
        queryByField('business_name_phonetic', phoneticSet),
      ]);

      // 2. Kandidaten-Gruppen pro Importzeile bauen.
      const groups = [];
      let truncated = false;
      for (const meta of rowsMeta) {
        if (groups.length >= MAX_GROUPS) { truncated = true; break; }

        const candById = new Map();
        const addCandidate = (entry, matchType) => {
          const score = nameSimilarity(meta.name, entry.data.business_name || '');
          const existing = candById.get(entry.id);
          if (existing) {
            if (score > existing.score) existing.score = score;
            return;
          }
          candById.set(entry.id, { id: entry.id, data: entry.data, score, matchType });
        };

        // Stark-exakte Kandidaten (hohe Präzision) → immer aufnehmen.
        (strongMap.get(meta.strong) || []).forEach(e => addCandidate(e, 'strong'));

        // Phonetische Kandidaten (Schreibvarianten) → nur bei genügend Ähnlichkeit.
        if (meta.phonetic && meta.phonetic.length >= MIN_KEY_LEN) {
          (phoneticMap.get(meta.phonetic) || []).forEach(e => {
            const score = nameSimilarity(meta.name, e.data.business_name || '');
            if (score >= SIMILARITY_THRESHOLD) addCandidate(e, 'phonetic');
          });
        }

        if (candById.size === 0) continue;

        const candidates = Array.from(candById.values())
          .sort((a, b) => b.score - a.score)
          .slice(0, MAX_CANDIDATES_PER_ROW);

        groups.push({
          rowIndex: meta.rowIndex,
          row: rowsToImport[meta.rowIndex],
          candidates,
        });
      }

      console.log(`Found ${groups.length} possible similar match group(s)${truncated ? ' (truncated)' : ''}`);
      return { groups, truncated };
    } catch (err) {
      // Bruchsicher: bei Fehlern KEINE Anzeige, Import bleibt komplett unverändert.
      console.error('Error checking for similar business names:', err);
      return empty;
    }
  };

  // Baut den additiven Anreicherungs-Patch für ein bestehendes Doc.
  //
  // GARANTIEN (bruchsicher):
  //   - Es werden NUR leere Felder gefüllt bzw. Mehrwert-Felder (E-Mail, Telefon,
  //     Socials) angehängt. Bestehende, nicht-leere Werte werden NIE überschrieben.
  //   - Status/Verlauf (lead_contacted, timestamp, flow_*, Tags, ...) werden NIE
  //     angefasst, da sie weder in APPEND_FIELDS noch FILL_EMPTY_FIELDS stehen.
  //   - Gibt {} zurück, wenn es nichts anzureichern gibt → dann wird gar nicht
  //     geschrieben.
  const buildEnrichmentPatch = (existing, row) => {
    const APPEND_FIELDS = [
      'lead_email', 'lead_telephone', 'lead_instagram', 'lead_linkedin',
      'lead_facebook', 'lead_tiktok', 'lead_twitter', 'lead_youtube',
    ];
    const FILL_EMPTY_FIELDS = [
      'business_name', 'lead_website', 'lead_address', 'lead_city',
      'lead_postcode', 'lead_country', 'lead_description', 'lead_decision_makers',
      'lead_image', 'lead_mapsurl', 'lead_link', 'lead_firstname', 'lead_lastname',
    ];
    const isEmptyVal = (v) => v === undefined || v === null
      || String(v).trim() === '' || String(v).trim() === '0';

    const patch = {};

    for (const f of APPEND_FIELDS) {
      if (isEmptyVal(row[f])) continue;
      const newVal = String(row[f]).trim();
      const existVal = existing[f] != null ? String(existing[f]).trim() : '';
      if (!existVal) {
        patch[f] = newVal;
      } else if (!existVal.toLowerCase().includes(newVal.toLowerCase())) {
        patch[f] = `${existVal}, ${newVal}`;
      }
    }

    for (const f of FILL_EMPTY_FIELDS) {
      if (isEmptyVal(row[f])) continue;
      if (isEmptyVal(existing[f])) patch[f] = String(row[f]).trim();
    }

    return patch;
  };

  // Zeige/verstecke Duplikate
  const toggleDuplicates = () => {
    setShowDuplicates(!showDuplicates);
  };

  // Zeige/verstecke doppelte Domains
  const toggleDuplicateDomains = () => {
    setShowDuplicateDomains(!showDuplicateDomains);
  };

  // Zeige/verstecke doppelte Business-Namen
  const toggleDuplicateBusinesses = () => {
    setShowDuplicateBusinesses(!showDuplicateBusinesses);
  };

  // Zeige/verstecke blockierte E-Mails
  const toggleBlacklisted = () => {
    setShowBlacklisted(!showBlacklisted);
  };

  // Robuste E-Mail-Normalisierung für konsistente Vergleiche
  const normalizeEmailForComparison = (email) => {
    if (!email || typeof email !== 'string') return '';
    
    return email
      .trim()
      .toLowerCase()
      // Unicode-Normalisierung (konvertiert verschiedene Darstellungen des gleichen Zeichens)
      .normalize('NFKC')
      // Entferne alle unsichtbaren Zeichen (Zero-Width Spaces, etc.)
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Ersetze alle Arten von Leerzeichen durch normale Leerzeichen
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Prüfe ob ein String als Firestore Document-ID sicher ist
  // Firestore-Doc-IDs dürfen kein "/" enthalten und nicht ".." sein
  const isValidDocId = (id) => {
    if (!id || typeof id !== 'string') return false;
    if (id.includes('/')) return false;       // "/" wird als Pfad-Trennzeichen interpretiert
    if (id === '.' || id === '..') return false; // Firestore-reserviert
    if (new Blob([id]).size > 1500) return false; // Max 1500 Bytes
    return true;
  };

  // Extract domain ("@firma.de") from a normalized email — used for matching
  // against domain-level blacklist entries.
  const extractDomainFromEmail = (email) => {
    if (!email || typeof email !== 'string') return '';
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return '';
    return email.substring(atIndex).toLowerCase();
  };

  // Prüfe auf blockierte E-Mail-Adressen (E-Mails UND Domains).
  // Uses the `checkBlacklistBatch` Cloud Function so reads scale with the
  // CSV size, not with the blacklist size. A 5k-lead upload across 500
  // domains costs ~5.5k reads regardless of whether the blacklist holds
  // 1k or 1M entries.
  const checkBlacklistedEmails = async (parsedData) => {
    if (!userId) return [];

    try {
      // Build the unique list of normalized emails up-front so the network
      // payload is as small as possible.
      const uniqueEmails = new Set();
      for (const item of parsedData) {
        if (!item || !item.lead_email || !isValidEmail(item.lead_email)) continue;
        const normalized = normalizeEmailForComparison(item.lead_email);
        if (normalized) uniqueEmails.add(normalized);
      }

      if (uniqueEmails.size === 0) {
        setBlacklistedEmails([]);
        setBlacklistedCount(0);
        return [];
      }

      console.log(`Checking ${uniqueEmails.size} unique emails against blacklist (server-side)...`);

      const { emails: blockedEmailSet, domains: blockedDomainSet } =
        await lookupBlacklistForEmails([...uniqueEmails], userId);

      if (blockedEmailSet.size === 0 && blockedDomainSet.size === 0) {
        setBlacklistedEmails([]);
        setBlacklistedCount(0);
        return [];
      }

      // Resolve each input email against the returned exact + domain hits.
      const blacklisted = [];
      uniqueEmails.forEach(normalizedEmail => {
        const isEmailBlocked = blockedEmailSet.has(normalizedEmail);
        const emailDomain = extractDomainFromEmail(normalizedEmail);
        const isDomainBlocked = emailDomain && blockedDomainSet.has(emailDomain);
        if (isEmailBlocked || isDomainBlocked) {
          blacklisted.push(normalizedEmail);
        }
      });

      console.log(`Found ${blacklisted.length} blacklisted emails in import data ` +
        `(${blockedEmailSet.size} exact-email blocks, ${blockedDomainSet.size} domain blocks matched).`);

      setBlacklistedEmails(blacklisted);
      setBlacklistedCount(blacklisted.length);

      return blacklisted;
    } catch (error) {
      console.error('Error checking for blacklisted emails:', error);
      // Fail closed for visibility but don't block the import — the sender
      // and outreach functions also enforce the blacklist downstream.
      setBlacklistedEmails([]);
      setBlacklistedCount(0);
      return [];
    }
  };

  // Preflight: stellt sicher, dass alle bestehenden Docs ein lead_email_dedup
  // und business_name_dedup Feld haben. Ohne diese Felder würde die neue
  // where('...', 'in', [...])-basierte Duplikatsprüfung alte Docs übersehen
  // (= doppelte Leads landen in der DB).
  //
  // Kontrakt:
  //   - Idempotent; der "Done"-Flag wird NUR gesetzt wenn die Migration 100%
  //     durchgelaufen ist UND ein anschließender Stats-Check 0 fehlende Felder
  //     zeigt. Halbmigrierte Zustände werden nicht gecached.
  //   - Bei Fehlern wird der Upload-Flow NICHT blockiert; ein Warn-Log wird
  //     geschrieben und der User kann den "Speed up"-Button manuell erneut
  //     drücken.
  const ensureDedupIndexReady = async () => {
    if (!userId) return true;
    const cacheKey = `bonusclub.dedupMigrationDone.${userId}`;
    if (typeof window !== 'undefined' && window.localStorage?.getItem(cacheKey) === '1') {
      return true;
    }

    const statsCallable = httpsCallable(getFunctions(), 'dedupBackfillStats');

    try {
      // userId MITSCHICKEN, damit wir in Team-Workspaces auf die Collection
      // des Owners (nicht des angemeldeten Members) schauen.
      const beforeResp = await statsCallable({ userId });
      const beforeStats = beforeResp.data?.stats || {};
      const missing = (beforeStats.missingEmailDedup || 0) + (beforeStats.missingBizDedup || 0);

      if (missing === 0) {
        try { window.localStorage?.setItem(cacheKey, '1'); } catch (_) {}
        return true;
      }

      console.log(`[Dedup Preflight] ${missing} dedup fields missing — running migration inline`);
      const result = await runDedupMigration();
      if (!result.ok) {
        // Migration nicht sauber durch — Flag NICHT setzen, damit beim nächsten
        // Öffnen erneut versucht wird. Wir lassen den Upload trotzdem weiterlaufen
        // (best-effort) — die neue Duplikat-Prüfung sieht dann halt nur die
        // bereits migrierten Docs.
        console.warn('[Dedup Preflight] migration did not complete cleanly:', result);
        return false;
      }

      // Verify: Stats erneut abfragen, Flag nur setzen wenn wirklich 0 fehlen.
      // Schützt vor Race-Conditions wenn parallel noch Writes reinkommen.
      const afterResp = await statsCallable({ userId });
      const afterStats = afterResp.data?.stats || {};
      const stillMissing = (afterStats.missingEmailDedup || 0) + (afterStats.missingBizDedup || 0);

      if (stillMissing === 0) {
        try { window.localStorage?.setItem(cacheKey, '1'); } catch (_) {}
        return true;
      }

      console.warn(`[Dedup Preflight] ${stillMissing} fields still missing after migration — flag NOT set, will retry next time`);
      return false;
    } catch (err) {
      // Wenn der Preflight fehlschlägt (z.B. Function noch nicht deployt),
      // NICHT den ganzen Flow blockieren — stattdessen Warnung loggen. Flag
      // bleibt ungesetzt, wird beim nächsten Mal wieder versucht.
      console.warn('[Dedup Preflight] failed — proceeding anyway:', err);
      return false;
    }
  };

  // Modifizierte handleCheckDuplicates-Funktion
  const handleCheckDuplicates = async () => {
    // Check if a user is logged in
    if (!userId) {
      console.error('No user logged in. Data cannot be saved.');
      return;
    }

    // Set importing state to true to show the loading spinner
    setImporting(true);
    setDupCheckComplete(false);
    setInternalDuplicateCount(0); // Reset duplicate count

    // Kleine Verzögerung für UI-Update
    await new Promise(resolve => setTimeout(resolve, 10));

    // Preflight: Dedup-Index muss auf allen alten Docs gesetzt sein, bevor
    // die neue where('...', 'in', [...])-Prüfung aussagekräftig ist.
    await ensureDedupIndexReady();

    try {
      // Parse CSV-Daten
      const parsedData = parseCsvData();
      console.log(`Initial data count: ${parsedData.length}`);
      
      // Entferne interne Duplikate
      const uniqueParsedData = removeInternalDuplicates(parsedData);
      console.log(`After removing internal duplicates: ${uniqueParsedData.length}`);

      // Wenn "email_only" Option gewählt wurde, filtere Einträge ohne gültige E-Mail-Adresse
      let filteredByEmailOption = uniqueParsedData;
      if (importOption === 'email_only') {
        filteredByEmailOption = uniqueParsedData.filter(item => 
          item.lead_email && isValidEmail(item.lead_email)
        );
        console.log(`After filtering for valid emails: ${filteredByEmailOption.length}`);
      }

      // Überprüfe auf blockierte E-Mail-Adressen (Blacklist)
      const blacklistedEmails = await checkBlacklistedEmails(filteredByEmailOption);
      
      // Überprüfe auf doppelte E-Mail-Adressen
      const duplicateEmails = await checkDuplicateEmails(filteredByEmailOption);
      
      // Überprüfe auf doppelte Business-Namen (nur für Einträge, die nicht bereits als E-Mail-Duplikate erkannt wurden)
      const duplicateBusinesses = await checkDuplicateBusinessNames(filteredByEmailOption, duplicateEmails);

      // Phase 1: Überprüfe auf bereits existierende Firmen-Domains (nur neue Docs
      // mit lead_domain_dedup; Legacy bleibt unberührt).
      const { enrichByDomain, skipDomains } = await checkDuplicateDomains(filteredByEmailOption);
      
      // Each record gets a timestamp and import name (filename)
      // Build per-stage template map (only include stages that have a selection)
      const stageMap = {};
      if (importTemplate) stageMap.intro = importTemplate;
      Object.entries(importTemplateStages).forEach(([stage, tplId]) => {
        if (tplId) stageMap[stage] = tplId;
      });
      const hasStageMap = Object.keys(stageMap).length > 0;

      const enhancedData = filteredByEmailOption.map(row => ({
        ...row,
        timestamp: new Date().toISOString(),
        import_name: fileName,
        ...(importTemplate ? { lead_template: importTemplate } : {}),
        ...(hasStageMap ? { lead_templates: stageMap } : {})
      }));

      // Anreicherungs-Plan für diesen Durchlauf zurücksetzen.
      const enrichmentPlan = new Map(); // existingDocId -> { existingData, rows: [] }

      // Filtere Einträge mit blockierten E-Mails, doppelten E-Mail-Adressen oder Business-Namen
      const filteredData = enhancedData.filter(item => {
        // Bestehende Logik unverändert: entscheide zuerst nach E-Mail/Business/Blacklist.
        let keepBase;
        if (item.lead_email && isValidEmail(item.lead_email)) {
          const normalizedEmail = normalizeEmailForComparison(item.lead_email);
          // Prüfe ob E-Mail blockiert ist ODER ein Duplikat ist
          keepBase = !blacklistedEmails.includes(normalizedEmail) && !duplicateEmails.includes(normalizedEmail);
        } else if (item.business_name && item.business_name.trim() !== '') {
          const normalizedBusinessName = normalizeBusinessName(item.business_name);
          keepBase = !duplicateBusinesses.includes(normalizedBusinessName);
        } else {
          keepBase = false;
        }

        if (!keepBase) return false;

        // Phase 1b (additiv, bruchsicher): Firmen-Domain-Abgleich.
        // Zeilen ohne echte Firmen-Domain (domain === '') sind nie betroffen.
        const domain = computeDomainDedup(item);
        if (domain) {
          const existing = enrichByDomain.get(domain);
          if (existing) {
            // Eindeutiger Treffer → bestehenden Eintrag anreichern statt neu anlegen.
            // Der bestehende (ggf. kontaktierte) Eintrag behält Status & Verlauf.
            if (!enrichmentPlan.has(existing.id)) {
              enrichmentPlan.set(existing.id, { existingData: existing.data, rows: [] });
            }
            enrichmentPlan.get(existing.id).rows.push(item);
            return false; // nicht als neues Doc importieren
          }
          if (skipDomains.has(domain)) {
            // Mehrdeutig (Domain auf mehreren bestehenden Docs) → sicher überspringen.
            return false;
          }
        }

        return true;
      });

      // Plan für confirmAndImport bereitstellen.
      enrichmentPlanRef.current = enrichmentPlan;
      setEnrichmentCount(enrichmentPlan.size);

      // Debug-Log
      console.log(`Final entries to import: ${filteredData.length}`);
      if (filteredData.length > 0) {
        console.log("Sample entries to be imported:");
        filteredData.slice(0, 3).forEach((item, idx) => {
          console.log(`Item ${idx + 1}:`, 
            item.lead_email ? `Email: ${item.lead_email}` : 'No email', 
            item.business_name ? `Business: ${item.business_name}` : 'No business name'
          );
        });
      }

      // Speichere gefilterte Daten für den späteren Import
      setFilteredData(filteredData);

      // Additiv: "mögliche ähnliche Treffer" über der bereits gefilterten Menge
      // ermitteln (läuft NUR auf den Zeilen, die ohnehin importiert würden, also
      // garantiert nicht bereits per E-Mail/Business/Domain erkannt). Bruchsicher:
      // Default-Entscheidung = importieren → Verhalten bleibt exakt wie bisher.
      try {
        const { groups: simGroups, truncated: simTruncated } =
          await checkSimilarBusinessNames(filteredData);
        setSimilarGroups(simGroups);
        setSimilarTruncated(simTruncated);
        const initialDecisions = {};
        simGroups.forEach(g => { initialDecisions[g.rowIndex] = 'import'; });
        setSimilarDecisions(initialDecisions);
        setShowSimilarMatches(true);
      } catch (simErr) {
        console.error('Similar-match check failed (non-fatal):', simErr);
        setSimilarGroups([]);
        setSimilarTruncated(false);
        setSimilarDecisions({});
      }

      // Prüfung abgeschlossen, wechsle zum Bestätigungsschritt
      setDupCheckComplete(true);
      setImporting(false);
      setStep(3);
    } catch (error) {
      console.error('Error during duplicate check:', error);
      setImporting(false);
    }
  };

  // Bestätigen und endgültig importieren
  const confirmAndImport = async () => {
    setIsUploading(true);
    setImportProgress(0);
    await new Promise(resolve => setTimeout(resolve, 10));

    // Additiv: Zeilen, die der User in der Ähnlichkeits-Review als Duplikat
    // markiert hat, vom Import ausschließen. Default (keine/`import`-Entscheidung)
    // ⇒ Zeile wird importiert → Verhalten unverändert. Indizes beziehen sich auf
    // `filteredData` (zwischen Check und Import unverändert).
    const dataToImport = filteredData.filter(
      (_, idx) => similarDecisions[idx] !== 'skip'
    );

    // Liste benötigter Referenzen für Rollback
    let importedDocRefs = [];

    try {
      // BEST PRACTICE: Define which fields should be treated as numbers for correct sorting.
      const numericFields = [
        'lead_rating',
        'lead_reviews',
        'lead_stars',
        'lead_instagram_followers',
      ];

      let successfulImports = 0;

      // PRÄVENTION: Import-Metadaten VORHER speichern, damit auch bei Abbruch
      // der Import in der Liste sichtbar ist und gelöscht werden kann
      if (fileName) {
        const templateName = importTemplate
          ? (customTemplates.find(t => t.id === importTemplate)?.topic || '')
          : '';
        const importNameRef = doc(firestore, `World/${userId}/import_names`, fileName);
        await setDoc(importNameRef, { 
          name: fileName, 
          last_imported: new Date().toISOString(),
          imported_count: 0,
          expected_count: dataToImport.length,
          source: 'csv_upload',
          status: 'in_progress',
          ...(templateName ? { template_name: templateName } : {}),
        }, { merge: true });
      }

      const CHUNK_SIZE = 40;

      // Hilfsfunktion zum Teilen des Arrays
      const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      const chunks = chunkArray(dataToImport, CHUNK_SIZE);

      if (dataToImport.length > 0) {
        const descCount = dataToImport.filter(r => r.lead_description).length;
        const bizCount = dataToImport.filter(r => r.business_name).length;
        console.log(`[confirmAndImport] ${bizCount}/${dataToImport.length} have business_name, ${descCount}/${dataToImport.length} have lead_description`);
        if (dataToImport[0]) {
          console.log(`[confirmAndImport] Sample row[0]:`, JSON.stringify({
            business_name: dataToImport[0].business_name || '',
            lead_description: dataToImport[0].lead_description || '',
            lead_email: dataToImport[0].lead_email || ''
          }));
        }
      }

      for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
        const chunk = chunks[chunkIdx];
        const batch = writeBatch(firestore);

        for (const row of chunk) {
          // Create a mutable copy of the row data for processing
          const mappedRow = { ...row };

          // 0. Sanitizer für problematische Platzhalterwerte ("0", "null", "NULL")
          const sanitize = (val) => {
            if (val === undefined || val === null) return "";
            const str = String(val).trim();
            if (str === "0" || str.toLowerCase() === "null") return "";
            return str;
          };

          // Speziell sensible Felder bereinigen
          mappedRow.lead_email = sanitize(mappedRow.lead_email);
          mappedRow.lead_website = sanitize(mappedRow.lead_website);
          mappedRow.lead_instagram = sanitize(mappedRow.lead_instagram);

          // 1. Standardwerte für alle Felder setzen, die fehlen
          // dm_ and _smart_ fields are transient mapping helpers, not persisted to Firestore
          for (const key in availableFields) {
              if (key.startsWith('dm_') || key.startsWith('_')) continue;
              if (!mappedRow.hasOwnProperty(key)) {
                  mappedRow[key] = numericFields.includes(key) ? 0 : "";
              }
          }

          // 2. Numerische Felder konvertieren (falls sie als String gespeichert waren)
          for (const field of numericFields) {
              if (mappedRow.hasOwnProperty(field)) {
                  const value = parseFloat(mappedRow[field]);
                  mappedRow[field] = isNaN(value) ? 0 : value;
              }
          }

          // 3. Normalisierte Felder für die Suche hinzufügen
          // Business-Name und Description zusammenführen (in Kleinbuchstaben) für eine vereinheitlichte Suche
          const namePart = mappedRow.business_name ? mappedRow.business_name.toLowerCase() : "";
          mappedRow.business_name_normalized = namePart;
          
          // 3b. TRIGRAM-INDEX für echte Teilstring-Suche in Firestore
          // Beispiel: "Wassermann360" → ["was", "ass", "sse", ..., "360"]
          mappedRow.search_terms = buildSearchTerms(mappedRow);

          // 4. Standard-Status setzen, falls nicht vorhanden
          if (!mappedRow.hasOwnProperty('lead_contacted')) {
              mappedRow.lead_contacted = "Not Contacted";
          }

          // 4b. Flow-Sortier-Priorität setzen (3 = kein Flow)
          if (!mappedRow.hasOwnProperty('flow_sort_priority')) {
              mappedRow.flow_sort_priority = 3;
          }

          // 5. Felder für spezielle Filter hinzufügen
          const hasWebsite = !!(mappedRow.lead_website && mappedRow.lead_website.trim());
          const emailValue = mappedRow.lead_email;
          const hasEmail = !!(emailValue && typeof emailValue === 'string' && /\S+@\S+\.\S+/.test(emailValue));
          // Sync lead_instagram_username → lead_instagram if only username was mapped
          if (!mappedRow.lead_instagram && mappedRow.lead_instagram_username) {
            mappedRow.lead_instagram = mappedRow.lead_instagram_username;
          }

          const hasInstagram = !!(mappedRow.lead_instagram && mappedRow.lead_instagram.trim());

          // Build lead_socials JSON from individual social fields (mirrors SocialEditModal save logic)
          const socialsObj = {};
          const socialFieldMap = {
            lead_linkedin: 'linkedin', lead_instagram: 'instagram', lead_facebook: 'facebook',
            lead_tiktok: 'tiktok', lead_twitter: 'x', lead_youtube: 'youtube',
            lead_xing: 'xing', lead_website: 'website',
          };
          for (const [field, key] of Object.entries(socialFieldMap)) {
            if (mappedRow[field] && mappedRow[field].trim()) socialsObj[key] = mappedRow[field].trim();
          }
          if (Object.keys(socialsObj).length > 0) {
            mappedRow.lead_socials = JSON.stringify(socialsObj);
          }

          mappedRow.is_ready_for_spy = hasWebsite && !hasEmail;
          mappedRow.has_website = hasWebsite;
          mappedRow.has_email = hasEmail;
          mappedRow.has_instagram = hasInstagram;

          // Add timestamp and import name
          mappedRow.timestamp = new Date().toISOString();
          mappedRow.import_name = fileName;

          // -------- Race-safe write using deterministic ID within same transaction --------
          const normalizedEmail = hasEmail ? normalizeEmailForComparison(emailValue) : null;
          const normalizedBiz = mappedRow.business_name ? normalizeBusinessName(mappedRow.business_name) : null;

          // Dedup-Felder persistieren, damit die Duplikat-Prüfung zukünftig mit
          // where('lead_email_dedup', 'in', [...]) laufen kann statt die ganze
          // Collection zu ziehen. Siehe functions/backfillDedupFields.js für die
          // einmalige Migration bestehender Docs.
          mappedRow.lead_email_dedup = normalizedEmail || '';
          mappedRow.business_name_dedup = normalizedBiz || '';
          // Phase 0 (additiv): normalisierte Firmen-Domain für künftiges Domain-Dedup.
          mappedRow.lead_domain_dedup = computeDomainDedup(mappedRow);
          // Additiv (Fuzzy-Layer): stark normalisierter + phonetischer Firmenname
          // für die "mögliche ähnliche Treffer"-Anzeige. Beeinflusst KEIN
          // bestehendes Matching und keine Doc-ID.
          mappedRow.business_name_strong = mappedRow.business_name
            ? normalizeBusinessNameStrong(mappedRow.business_name) : '';
          mappedRow.business_name_phonetic = mappedRow.business_name
            ? computeNamePhonetic(mappedRow.business_name) : '';

          let docRef;
          const emailDocId = normalizedEmail ? `e_${normalizedEmail}` : null;
          const bizDocId = normalizedBiz ? `b_${normalizedBiz}` : null;

          if (emailDocId && isValidDocId(emailDocId)) {
            // Deterministische ID basierend auf E-Mail (für Duplikat-Vermeidung)
            docRef = doc(firestore, `World/${userId}/db`, emailDocId);
          } else if (bizDocId && isValidDocId(bizDocId)) {
            // Deterministische ID basierend auf Business-Name
            docRef = doc(firestore, `World/${userId}/db`, bizDocId);
          } else {
            // Fallback: Auto-generierte ID (wenn E-Mail/Business ungültige Zeichen enthält)
            docRef = doc(collection(firestore, `World/${userId}/db`));
          }

          batch.set(docRef, mappedRow);
          importedDocRefs.push(docRef);
        }

        await batch.commit();
        successfulImports += chunk.length;
        setImportProgress(Math.round((successfulImports / dataToImport.length) * 100));

        // Kleine Pause zwischen Batches um den Firestore Write-Stream nicht zu überlasten
        if (chunkIdx < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // -------- Phase 1b: Anreicherung bestehender Einträge (additiv) --------
      // Läuft NACH dem Anlegen neuer Docs und in EIGENEM try/catch: ein Fehler
      // hier darf den Import niemals abbrechen oder zurückrollen. Es werden nur
      // leere Felder gefüllt / E-Mails angehängt — Status & Verlauf bleiben.
      try {
        const plan = enrichmentPlanRef.current;
        if (plan && plan.size > 0) {
          const planEntries = Array.from(plan.entries());
          const ENRICH_BATCH_SIZE = 200;
          let enriched = 0;

          for (let i = 0; i < planEntries.length; i += ENRICH_BATCH_SIZE) {
            const slice = planEntries.slice(i, i + ENRICH_BATCH_SIZE);
            const enrichBatch = writeBatch(firestore);
            let pending = 0;

            for (const [existingId, { existingData, rows }] of slice) {
              // Mehrere Import-Zeilen derselben Domain nacheinander einfalten,
              // damit Anhänge (z. B. 2. E-Mail) sich akkumulieren.
              const working = { ...existingData };
              const combinedPatch = {};
              for (const row of rows) {
                const patch = buildEnrichmentPatch(working, row);
                Object.assign(working, patch);
                Object.assign(combinedPatch, patch);
              }

              if (Object.keys(combinedPatch).length === 0) continue;

              // Abgeleitete/Index-Felder konsistent halten (aus dem Merge-Ergebnis).
              combinedPatch.business_name_normalized = working.business_name
                ? String(working.business_name).toLowerCase() : '';
              combinedPatch.has_email = isValidEmail(working.lead_email);
              combinedPatch.has_website = !!(working.lead_website && String(working.lead_website).trim());
              combinedPatch.has_instagram = !!(working.lead_instagram && String(working.lead_instagram).trim());
              combinedPatch.is_ready_for_spy = combinedPatch.has_website && !combinedPatch.has_email;
              combinedPatch.search_terms = buildSearchTerms(working);
              combinedPatch.lead_domain_dedup = computeDomainDedup(working);

              // E-Mail-Dedup-Index nachziehen, falls bestehendes Doc bisher keine
              // E-Mail hatte (damit künftige E-Mail-Dedup-Prüfungen greifen).
              const hadEmailDedup = typeof existingData.lead_email_dedup === 'string'
                && existingData.lead_email_dedup !== '';
              if (combinedPatch.has_email && !hadEmailDedup) {
                const firstEmail = String(working.lead_email).split(',')[0].trim();
                if (isValidEmail(firstEmail)) {
                  combinedPatch.lead_email_dedup = normalizeEmailForComparison(firstEmail);
                }
              }

              // Business-Name-Dedup-Index nachziehen, falls neu befüllt.
              const hadBizDedup = typeof existingData.business_name_dedup === 'string'
                && existingData.business_name_dedup !== '';
              if (combinedPatch.business_name && !hadBizDedup) {
                combinedPatch.business_name_dedup = normalizeBusinessName(working.business_name);
              }
              // Fuzzy-Felder (additiv) nachziehen, sobald ein Business-Name neu
              // gefüllt wurde — hält angereicherte Docs für die Ähnlichkeits-
              // Anzeige auffindbar. Verändert kein bestehendes Matching.
              if (combinedPatch.business_name) {
                combinedPatch.business_name_strong = normalizeBusinessNameStrong(working.business_name);
                combinedPatch.business_name_phonetic = computeNamePhonetic(working.business_name);
              }

              combinedPatch.last_enriched_at = new Date().toISOString();
              combinedPatch.last_enriched_source = 'csv_import';

              const existingRef = doc(firestore, `World/${userId}/db`, existingId);
              enrichBatch.set(existingRef, combinedPatch, { merge: true });
              pending++;
              enriched++;
            }

            if (pending > 0) await enrichBatch.commit();
          }

          console.log(`[confirmAndImport] Enriched ${enriched} existing contact(s) via domain match`);
        }
      } catch (enrichErr) {
        // Bruchsicher: Anreicherungs-Fehler werden nur geloggt, niemals geworfen.
        console.error('[confirmAndImport] Enrichment step failed (non-fatal):', enrichErr);
      }

      if (fileName) {
        const templateName = importTemplate
          ? (customTemplates.find(t => t.id === importTemplate)?.topic || '')
          : '';
        const importNameRef = doc(firestore, `World/${userId}/import_names`, fileName);
        await setDoc(importNameRef, { 
          name: fileName, 
          last_imported: new Date().toISOString(),
          imported_count: successfulImports,
          source: 'csv_upload',
          status: 'complete',
          ...(templateName ? { template_name: templateName } : {}),
        }, { merge: true });
      }

      // DSGVO: Speichere Consent-Log mit IP, Zeitstempel und User-Daten
      await saveConsentLog(fileName, successfulImports);

      // Signal classification is deferred — handled lazily when contacts
      // enter the viewport in NewWorldDbTable (prevents 10k+ writes on import).

      // Refresh page after import
      window.location.reload();

    } catch (error) {
      console.error('Error importing data:', error);
      // Rollback: versuche alle bereits importierten Dokumente zu löschen
      try {
        const BATCH_DELETE_SIZE = 100; // Kleine Batches für stabilen Rollback
        for (let i = 0; i < importedDocRefs.length; i += BATCH_DELETE_SIZE) {
          const slice = importedDocRefs.slice(i, i + BATCH_DELETE_SIZE);
          const batchDel = writeBatch(firestore);
          slice.forEach(refDoc => batchDel.delete(refDoc));
          await batchDel.commit();
          // Pause zwischen Rollback-Batches
          if (i + BATCH_DELETE_SIZE < importedDocRefs.length) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        console.log(`Rollback successful – removed ${importedDocRefs.length} docs`);
      } catch (rollbackErr) {
        console.error('Rollback failed:', rollbackErr);
      }

      alert('An error occurred during import. No contacts were imported. Please try again.');
    } finally {
      setIsUploading(false);
      setImporting(false);
    }
  };

  // Einmalige Migration starten — idempotent, kann beliebig oft erneut
  // angestoßen werden, falls die Function im 8min-Zeitfenster lief.
  //
  // Gibt { ok: boolean, stats } zurück, damit Aufrufer (z.B. der Preflight)
  // sauber zwischen "komplett fertig" und "abgebrochen/Fehler" unterscheiden
  // können. Nur bei ok === true darf der Done-Flag im localStorage gesetzt
  // werden — sonst würden wir einen halbmigrierten Zustand maskieren.
  const runDedupMigration = async () => {
    if (!userId) return { ok: false, reason: 'no-user' };
    if (migrationState === 'running') return { ok: false, reason: 'already-running' };

    setMigrationState('running');
    setMigrationStats(null);

    // Wichtig: Wir geben dem callable einen großzügigen Timeout, da die Default
    // 70 s der Firebase-JS-SDK auf großen Workspaces sonst noch vor dem
    // server-seitigen Cursor-Return greifen würden. Server liefert pro Call
    // einen partiellen Stand zurück (siehe MAX_RUNTIME_MS dort) — wir
    // continuieren über `cursor`, nicht über Re-Scan.
    const callable = httpsCallable(getFunctions(), 'backfillDedupFields', {
      timeout: 90000,
    });
    const aggregated = { scanned: 0, updated: 0, skipped: 0 };
    let doneByFunction = false;
    let safetyBreak = 0;
    let nextCursor = null;
    const MAX_CONTINUATIONS = 50; // 50 × ~6k docs/call ≈ 300k docs max

    try {
      while (!doneByFunction && safetyBreak < MAX_CONTINUATIONS) {
        safetyBreak += 1;
        setMigrationStats({ ...aggregated, continuation: safetyBreak });

        // userId MITSCHICKEN: in Team-Workspaces ist der angemeldete User
        // (auth.uid) NICHT der Owner der db-Collection. Ohne diesen Parameter
        // würde der Server auf der falschen Sub-Collection arbeiten.
        const payload = { userId };
        if (nextCursor) payload.startAfterId = nextCursor;

        const resp = await callable(payload);
        const s = (resp.data && resp.data.summary) || {};
        aggregated.scanned += s.scanned || 0;
        aggregated.updated += s.updated || 0;
        aggregated.skipped += s.skipped || 0;

        const results = Array.isArray(resp.data?.results) ? resp.data.results : [];
        const partial = results.find(r => r.done === false);
        if (partial) {
          // Server hat gestoppt (Time-Budget). Cursor mitnehmen und nochmal.
          nextCursor = partial.cursor || null;
          doneByFunction = false;
        } else {
          nextCursor = null;
          doneByFunction = true;
        }
      }

      if (!doneByFunction) {
        // Safety-break hit: VIEL zu viele Continuations (> 10). Kein echter
        // Erfolg — wir markieren es als partial und lassen den Flag UNGESETZT,
        // damit beim nächsten Öffnen erneut versucht wird.
        console.warn('[Dedup Migration] hit safety-break — partial only', aggregated);
        setMigrationState('error');
        setMigrationStats({ ...aggregated, error: 'Still more data than expected — try again.' });
        return { ok: false, reason: 'safety-break', stats: aggregated };
      }

      setMigrationState('done');
      setMigrationStats(aggregated);
      console.log('[Dedup Migration] complete:', aggregated);
      return { ok: true, stats: aggregated };
    } catch (err) {
      console.error('[Dedup Migration] failed:', err);
      setMigrationState('error');
      setMigrationStats({ ...aggregated, error: err.message || String(err) });
      return { ok: false, reason: 'exception', error: err };
    }
  };

  // Zurück zur Spaltenzuordnung
  const goBackToMapping = () => {
    setStep(2);
    setDupCheckComplete(false);
    setDuplicateCount(0);
    setDuplicateEmails([]);
    setDuplicateEmailMatches({});
    setDuplicateBusinessCount(0);
    setDuplicateBusinessNames([]);
    setDuplicateBusinessMatches({});
    setSimilarGroups([]);
    setSimilarDecisions({});
    setSimilarTruncated(false);
  };

  // Anzahl der in der Ähnlichkeits-Review als Duplikat markierten Zeilen.
  const similarSkippedCount = Object.values(similarDecisions).filter(v => v === 'skip').length;
  // Berechne die Gesamtzahl der zu importierenden Einträge (abzgl. manuell als
  // Duplikat markierter ähnlicher Treffer; Default = importieren).
  const totalImportCount = Math.max(0, filteredData.length - similarSkippedCount);
  const totalSkippedCount = duplicateCount + duplicateBusinessCount;

  // Toggle für eine einzelne Ähnlichkeits-Zeile (Importieren ⇄ als Duplikat überspringen).
  const setSimilarDecision = (rowIndex, decision) => {
    setSimilarDecisions(prev => ({ ...prev, [rowIndex]: decision }));
  };

  return (
    <div className="world-db-uploader-overlay">
      <div
        className={`world-db-uploader-popup${step >= 2 ? ' world-db-uploader-popup--wide' : ''}`}
        ref={modalRef}
      >
        <button className="world-db-uploader-close" onClick={onClose}>×</button>
  
        
        {step === 1 ? (
          <div className="world-db-uploader-step1">
            <div className="world-db-uploader-icon-container">
              <img src="/img/upload.svg" alt="Upload Icon" className="world-db-uploader-icon" />
            </div>
            <h2>Upload File</h2>
            <p>Select a CSV or TXT file to import your contacts</p>
            
            <div className={`world-db-uploader-consent-wrapper ${confirmedRights ? 'checked' : ''}`}>
              <div className="world-db-uploader-consent-row">
                <label className="world-db-uploader-consent-label">
                  <div 
                    className={`world-db-uploader-consent-checkbox ${confirmedRights ? 'checked' : ''}`}
                    onClick={() => setConfirmedRights(!confirmedRights)}
                  >
                    {confirmedRights && (
                      <img src="/img/tick.svg" alt="Check" className="world-db-uploader-tick-icon" />
                    )}
                  </div>
                  <span className="world-db-uploader-consent-text">
                    I confirm that I have all rights to use and upload this contact list and agree to the <a href="https://www.bonusclub.io/terms" target="_blank" rel="noopener noreferrer" className="world-db-uploader-terms-link">Terms of Service</a>.
                  </span>
                </label>
                
                <button 
                  className="world-db-uploader-info-button"
                  onClick={() => setShowLegalText(!showLegalText)}
                  type="button"
                >
                  ℹ
                </button>
              </div>
              
              {showLegalText && (
                <div className="world-db-uploader-legal-text">
                  By uploading this contact list, the user confirms that all data has been collected and is being used in full compliance with the GDPR, the ePrivacy Directive, the UK GDPR (if applicable), national data protection laws, and all relevant competition and contact regulations.
                  <br /><br />
                  The user bears sole and exclusive responsibility for the lawfulness of the data, including all required consents, permissions, information duties, and the legal basis for contacting or processing these individuals under Articles 5–6 GDPR and applicable national laws (including §7 UWG in Germany).
                  <br /><br />
                  BonusClub is not the data controller for user-uploaded contacts, assumes no duty to verify the lawfulness of the data, and accepts no liability for any violations, consequences, or damages arising from the user's use of the uploaded contacts.
                  <br /><br />
                  By uploading, the user agrees to fully indemnify and hold BonusClub harmless from any claims, complaints, or legal actions brought by third parties in connection with the user's uploaded data.
                </div>
              )}
            </div>
            
            <button 
              className={`world-db-uploader-select-btn ${!confirmedRights ? 'disabled' : ''}`}
              onClick={triggerFileInput}
              disabled={!confirmedRights}
            >
              Select File
            </button>
            <input 
              type="file" 
              ref={fileInputRef}
              accept=".csv,.txt" 
              onChange={handleFileChange} 
              style={{ display: 'none' }}
            />

            <div className="world-db-uploader-optimize">
              {migrationState === 'idle' && (
                <button
                  type="button"
                  className="world-db-uploader-optimize-btn"
                  onClick={runDedupMigration}
                  disabled={!userId}
                >
                  <i className="fa fa-bolt" aria-hidden="true" style={{ marginRight: 6 }}></i>
                  Speed up duplicate check (one‑time)
                </button>
              )}
              {migrationState === 'running' && (
                <div className="world-db-uploader-optimize-status running">
                  <span className="world-db-uploader-optimize-spinner" aria-hidden="true"></span>
                  Optimizing duplicate index…
                </div>
              )}
              {migrationState === 'done' && migrationStats && (
                <div className="world-db-uploader-optimize-status done">
                  <i className="fa fa-check" aria-hidden="true" style={{ marginRight: 6 }}></i>
                  Done — {migrationStats.updated} contacts indexed, {migrationStats.skipped} already up‑to‑date.
                </div>
              )}
              {migrationState === 'error' && (
                <div className="world-db-uploader-optimize-status error">
                  <i className="fa fa-exclamation-circle" aria-hidden="true" style={{ marginRight: 6 }}></i>
                  Optimization failed. Try again later.
                  <button
                    type="button"
                    className="world-db-uploader-optimize-retry"
                    onClick={runDedupMigration}
                  >
                    Retry
                  </button>
                </div>
              )}
              <p className="world-db-uploader-optimize-hint">
                Run once if your duplicate check feels slow. Skips contacts that are already indexed.
              </p>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="world-db-uploader-step2">
            <div className="world-db-uploader-step2-head">
              <h2>Map your Data</h2>
              <div className="world-db-uploader-filename">
                <span className="world-db-uploader-file-badge">
                  {fileName}
                </span>
                <span className="delimiter-info">
                  Delimiter: {delimiter === ';;' ? 'Double Semicolon' : delimiter === ';' ? 'Semicolon' : 'Comma'}
                </span>
              </div>
            </div>
            
            {delimiter === ';;' && (
              <div className="world-db-delimiter-hint">
                <div className="world-db-delimiter-hint-header">
                  <span><i className="fa fa-info-circle"></i> Double Semicolon Delimiter Detected</span>
                </div>
                <p>
                  This file uses double semicolons (;;) as a delimiter. 
                  We have automatically removed the empty columns.
                </p>
              </div>
            )}
            
            <div className="world-db-uploader-step2-body">
            <div className="world-db-mapping-container">
              {columns.map((col, index) => {
                const mappedKey = mappedColumns[index];
                const isNumericTarget = mappedKey && ['lead_rating', 'lead_reviews', 'lead_stars', 'lead_instagram_followers'].includes(mappedKey);
                const sampleVal = csvRows[0]?.[index];
                const hasNonNumericData = isNumericTarget && sampleVal && sampleVal.trim() !== '' && isNaN(parseFloat(sampleVal));
                return (
                  <div key={index} className={`world-db-mapping-row${mappedKey ? ' is-mapped' : ''}${hasNonNumericData ? ' world-db-mapping-row-warning' : ''}`}>
                    <div className="world-db-mapping-col-info">
                      <span className="world-db-column-name">{col}</span>
                      <span className="world-db-mapping-sample-value">
                        {sampleVal && String(sampleVal).trim()
                          ? `${String(sampleVal).substring(0, 40)}${String(sampleVal).length > 40 ? '...' : ''}`
                          : '— empty —'}
                      </span>
                      {hasNonNumericData && (
                        <span className="world-db-mapping-num-warning">Numbers only — text values will become 0</span>
                      )}
                    </div>
                    <span className="world-db-mapping-arrow" aria-hidden="true">→</span>
                    <select
                      onChange={(e) => handleMappingChange(index, e.target.value)}
                      value={mappedColumns[index] || ''}
                    >
                      <option value="">Select...</option>
                      {importFieldGroups.map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.fields.map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            <div className="world-db-import-options">
                  <h3>Import Options</h3>
                  <div className="world-db-import-option-row">
                    <label>
                      <input 
                        type="radio" 
                        name="importOption" 
                        value="all" 
                        checked={importOption === 'all'} 
                        onChange={() => setImportOption('all')} 
                      />
                      Import all entries (regardless of email)
                    </label>
                  </div>
                  <div className="world-db-import-option-row">
                    <label>
                      <input 
                        type="radio" 
                        name="importOption" 
                        value="email_only" 
                        checked={importOption === 'email_only'} 
                        onChange={() => setImportOption('email_only')} 
                      />
                      Import only entries with email
                    </label>
                  </div>

                <div className="world-db-import-strict-section">
                <div className="world-db-import-strict-title">Extra duplicate check (within this file)</div>
                <div className="world-db-import-option-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={dedupeByWebsiteActive}
                      disabled={!hasWebsiteColumn}
                      onChange={() => setDedupeByWebsite(v => !v)}
                    />
                    Skip rows with the same website
                    {!hasWebsiteColumn && (
                      <span className="world-db-import-strict-missing">no website column mapped</span>
                    )}
                  </label>
                </div>
                <div className="world-db-import-option-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={dedupeByPhoneActive}
                      disabled={!hasPhoneColumn}
                      onChange={() => setDedupeByPhone(v => !v)}
                    />
                    Skip rows with the same phone number
                    {!hasPhoneColumn && (
                      <span className="world-db-import-strict-missing">no phone column mapped</span>
                    )}
                  </label>
                </div>
                <p className="world-db-import-strict-hint">
                  Keeps only the first row per website / phone number. Rows without that
                  value are never affected. Note: multiple contacts of the same company
                  will be reduced to one.
                </p>
              </div>

              {customTemplates.length > 0 && (
                <div className="world-db-import-template-section">
                  <label className="world-db-import-template-toggle">
                    <div 
                      className={`world-db-import-template-checkbox ${importTemplate ? 'checked' : ''}`}
                      onClick={() => {
                        if (importTemplate) {
                          setImportTemplate('');
                          setImportTemplateStages({ follow_up_1: '', follow_up_2: '', follow_up_3: '' });
                          setShowStageTemplates(false);
                        } else {
                          setImportTemplate(customTemplates[0]?.id || '');
                        }
                      }}
                    >
                      {importTemplate && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      )}
                    </div>
                    <span>Assign Email Template</span>
                  </label>
                  {importTemplate && (
                    <>
                      <div className="world-db-import-template-stage-row">
                        <span className="world-db-import-template-stage-label">Intro Email</span>
                        <select
                          className="world-db-import-template-select"
                          value={importTemplate}
                          onChange={(e) => setImportTemplate(e.target.value)}
                        >
                          {customTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.topic}</option>
                          ))}
                        </select>
                      </div>

                      {!showStageTemplates ? (
                        <button
                          type="button"
                          className="world-db-import-template-expand-btn"
                          onClick={() => setShowStageTemplates(true)}
                        >
                          <i className="fa fa-plus-circle"></i> Customize Follow-Up Templates
                        </button>
                      ) : (
                        <div className="world-db-import-template-stages">
                          {[
                            { key: 'follow_up_1', label: 'Follow Up 1' },
                            { key: 'follow_up_2', label: 'Follow Up 2' },
                            { key: 'follow_up_3', label: 'Follow Up 3' },
                          ].map(stage => (
                            <div key={stage.key} className="world-db-import-template-stage-row">
                              <span className="world-db-import-template-stage-label">{stage.label}</span>
                              <select
                                className="world-db-import-template-select"
                                value={importTemplateStages[stage.key] || ''}
                                onChange={(e) => setImportTemplateStages(prev => ({
                                  ...prev,
                                  [stage.key]: e.target.value
                                }))}
                              >
                                <option value="">Same as Intro</option>
                                {customTemplates.map(t => (
                                  <option key={t.id} value={t.id}>{t.topic}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="world-db-import-template-collapse-btn"
                            onClick={() => {
                              setShowStageTemplates(false);
                              setImportTemplateStages({ follow_up_1: '', follow_up_2: '', follow_up_3: '' });
                            }}
                          >
                            <i className="fa fa-minus-circle"></i> Reset Follow-Up Templates
                          </button>
                        </div>
                      )}

                      <div className="world-db-import-template-hint">
                        {showStageTemplates && Object.values(importTemplateStages).some(v => v)
                          ? 'Each stage uses its own template. Stages set to "Same as Intro" use the intro template.'
                          : 'All imported contacts will use this template for every email stage.'
                        }
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Während die Preflight-Migration läuft, zeigen wir einen sichtbaren
                Hinweis — sonst wirkt der einzelne Button-Spinner wie "hängt". */}
            {importing && migrationState === 'running' && (
              <div className="world-db-uploader-migration-banner">
                <span className="world-db-uploader-optimize-spinner" aria-hidden="true"></span>
                <div className="world-db-uploader-migration-banner-text">
                  <strong>One‑time setup: indexing your contacts for faster duplicate checks</strong>
                  <span>
                    This only happens once for your account. You can leave this open.
                    {migrationStats && typeof migrationStats.updated === 'number' && (
                      <> {migrationStats.updated.toLocaleString()} contacts indexed so far.</>
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="world-db-uploader-actions">
              <button 
                type="button"
                className="altBtn altBtn--petrol world-db-uploader-back-btn" 
                onClick={() => setStep(1)}
                disabled={importing}
              >
                Back
              </button>
              <button
                type="button"
                className={`altBtn ${atLeastOneFieldAssigned ? 'altBtn--save' : ''} world-db-uploader-submit-btn ${
                  atLeastOneFieldAssigned ? 'active' : 'inactive'
                } ${importing ? 'loading' : ''}`}
                onClick={handleCheckDuplicates}
                disabled={!atLeastOneFieldAssigned || importing}
              >
                {importing
                  ? (migrationState === 'running'
                      ? 'Indexing contacts…'
                      : 'Checking duplicates...')
                  : 'Check Duplicates'}
              </button>
            </div>
          </div>
        ) : (
          <div className="world-db-uploader-step3">
            <h2>Import Preview</h2>
            <p className="world-db-uploader-filename">
              File: {fileName} <span className="delimiter-info">({csvRows.length} total entries)</span>
            </p>
            
            {blacklistedCount > 0 && (
              <div className="world-db-duplicate-info blacklisted">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    <i className="fa fa-ban" style={{ marginRight: '8px' }}></i>
                    {blacklistedCount} {blacklistedCount === 1 ? 'email' : 'emails'} blocked by blacklist
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleBlacklisted}
                  >
                    {showBlacklisted ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showBlacklisted && (
                  <div className="world-db-duplicate-list">
                    {blacklistedEmails.map((email, index) => (
                      <div key={index} className="world-db-duplicate-item">{email}</div>
                    ))}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  These email addresses are on your blacklist and will be skipped.
                </p>
              </div>
            )}

            {duplicateCount > 0 && (
              <div className="world-db-duplicate-info">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {duplicateCount} {duplicateCount === 1 ? 'duplicate' : 'duplicates'} with identical email found
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleDuplicates}
                  >
                    {showDuplicates ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showDuplicates && (
                  <div className="world-db-duplicate-list">
                    {duplicateEmails.map((email, index) => {
                      const matches = duplicateEmailMatches[email] || [];
                      return (
                        <div key={index} className="world-db-duplicate-item">
                          <span style={{ fontFamily: 'ui-monospace, monospace' }}>{email}</span>
                          {matches.length > 0 && (
                            <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>
                              → {matches.map((m) => m.businessName).join(', ')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  These email addresses already exist in the database and will be skipped.
                  The existing contact is shown after the arrow.
                </p>
              </div>
            )}

            {duplicateBusinessCount > 0 && (
              <div className="world-db-duplicate-info business">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {duplicateBusinessCount} {duplicateBusinessCount === 1 ? 'duplicate' : 'duplicates'} with identical business name found
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleDuplicateBusinesses}
                  >
                    {showDuplicateBusinesses ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showDuplicateBusinesses && (
                  <div className="world-db-duplicate-list">
                    {duplicateBusinessNames.map((businessName, index) => {
                      const matches = duplicateBusinessMatches[businessName] || [];
                      return (
                        <div key={index} className="world-db-duplicate-item">
                          <span>{businessName}</span>
                          {matches.length > 0 && (
                            <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>
                              → {matches.map((m) => m.businessName + (m.email ? ` (${m.email})` : '')).join(', ')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  These business names already exist in the database and will be skipped.
                  The existing contact is shown after the arrow.
                </p>
              </div>
            )}

            {duplicateDomainCount > 0 && (
              <div className="world-db-duplicate-info business">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {duplicateDomainCount} {duplicateDomainCount === 1 ? 'duplicate' : 'duplicates'} with identical company domain found
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleDuplicateDomains}
                  >
                    {showDuplicateDomains ? 'Hide' : 'Show'}
                  </button>
                </div>

                {showDuplicateDomains && (
                  <div className="world-db-duplicate-list">
                    {duplicateDomains.map((domain, index) => (
                      <div key={index} className="world-db-duplicate-item">{domain}</div>
                    ))}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  Contacts on these company domains already exist. The existing contact will be
                  enriched with any new info (its status &amp; history stay intact) instead of
                  creating a duplicate.
                </p>
              </div>
            )}

            {similarGroups.length > 0 && (
              <div className="world-db-similar-info">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {similarGroups.length} possible similar {similarGroups.length === 1 ? 'match' : 'matches'} found
                    {' '}— please review
                  </span>
                  <button
                    className="world-db-toggle-duplicates-btn"
                    onClick={() => setShowSimilarMatches(s => !s)}
                  >
                    {showSimilarMatches ? 'Hide' : 'Show'}
                  </button>
                </div>

                <p className="world-db-duplicate-message">
                  These look like existing companies with a slightly different name. They were NOT caught
                  by the email, business-name or domain checks. By default they are imported as new
                  contacts — mark any real duplicates to skip them.
                </p>

                {showSimilarMatches && (
                  <div className="world-db-similar-list">
                    {similarGroups.map((group) => {
                      const decision = similarDecisions[group.rowIndex] || 'import';
                      const isSkip = decision === 'skip';
                      const importedName = (group.row.business_name || '').trim() || '(no name)';
                      const incomingMeta = group.row.lead_email || group.row.lead_website
                        || group.row.lead_city || '';
                      return (
                        <div
                          key={group.rowIndex}
                          className={`world-db-similar-group ${isSkip ? 'is-skip' : ''}`}
                        >
                          <div className="world-db-similar-compare">
                            <div className="world-db-similar-side incoming">
                              <span className="world-db-similar-tag">Importing</span>
                              <span className="world-db-similar-name">{importedName}</span>
                              {incomingMeta && (
                                <span className="world-db-similar-meta">{incomingMeta}</span>
                              )}
                            </div>
                            <div className="world-db-similar-arrow">≈</div>
                            <div className="world-db-similar-side existing">
                              <span className="world-db-similar-tag">Already in database</span>
                              {group.candidates.map((c) => {
                                const candName = (c.data.business_name || '').trim() || '(no name)';
                                const candMeta = c.data.lead_email || c.data.lead_website
                                  || c.data.lead_city || '';
                                return (
                                  <div key={c.id} className="world-db-similar-candidate">
                                    <span className="world-db-similar-name">{candName}</span>
                                    <span className="world-db-similar-score">
                                      {Math.round(c.score * 100)}% match
                                    </span>
                                    {candMeta && (
                                      <span className="world-db-similar-meta">{candMeta}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="world-db-similar-actions">
                            <button
                              type="button"
                              className={`world-db-similar-choice ${!isSkip ? 'active' : ''}`}
                              onClick={() => setSimilarDecision(group.rowIndex, 'import')}
                            >
                              Import as new
                            </button>
                            <button
                              type="button"
                              className={`world-db-similar-choice danger ${isSkip ? 'active' : ''}`}
                              onClick={() => setSimilarDecision(group.rowIndex, 'skip')}
                            >
                              Duplicate — skip
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {similarTruncated && (
                      <p className="world-db-duplicate-message">
                        Showing the first {similarGroups.length} groups only to keep the preview fast.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="world-db-import-summary">
              <h3>Import Summary</h3>
              <div className="world-db-summary-stats">
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Total entries:</span>
                  <span className="world-db-summary-value">{csvRows.length}</span>
                </div>
                {internalDuplicateCount > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Internal duplicates:</span>
                    <span className="world-db-summary-value negative">{internalDuplicateCount}</span>
                  </div>
                )}
                {internalWebsiteDuplicateCount > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">↳ same website:</span>
                    <span className="world-db-summary-value negative">{internalWebsiteDuplicateCount}</span>
                  </div>
                )}
                {internalPhoneDuplicateCount > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">↳ same phone number:</span>
                    <span className="world-db-summary-value negative">{internalPhoneDuplicateCount}</span>
                  </div>
                )}
                {importOption === 'email_only' && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Entries without valid email:</span>
                    <span className="world-db-summary-value negative">
                      {csvRows.filter(row => {
                        const emailIndex = Object.keys(mappedColumns).find(
                          key => mappedColumns[key] === 'lead_email'
                        );
                        if (!emailIndex || !row[emailIndex]) return true;
                        const emailValue = row[emailIndex].trim();
                        return !isValidEmail(emailValue);
                      }).length}
                    </span>
                  </div>
                )}
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Blacklisted emails:</span>
                  <span className="world-db-summary-value negative">{blacklistedCount}</span>
                </div>
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Email duplicates:</span>
                  <span className="world-db-summary-value negative">{duplicateCount}</span>
                </div>
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Business name duplicates:</span>
                  <span className="world-db-summary-value negative">{duplicateBusinessCount}</span>
                </div>
                {duplicateDomainCount > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Company domain matches (enriched):</span>
                    <span className="world-db-summary-value negative">{duplicateDomainCount}</span>
                  </div>
                )}
                {similarGroups.length > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Possible similar matches (review):</span>
                    <span className="world-db-summary-value">
                      {similarGroups.length}{similarSkippedCount > 0 ? ` (${similarSkippedCount} marked as duplicate)` : ''}
                    </span>
                  </div>
                )}
                <div className="world-db-summary-row total">
                  <span className="world-db-summary-label">Entries to import:</span>
                  <span className="world-db-summary-value positive">{totalImportCount}</span>
                </div>
                <div className="world-db-summary-row import-option">
                  <span className="world-db-summary-label">Import option:</span>
                  <span className="world-db-summary-value">
                    {importOption === 'all' ? 'All entries' : 'Only entries with email'}
                  </span>
                </div>
                {importTemplate && (
                  <>
                    <div className="world-db-summary-row import-option">
                      <span className="world-db-summary-label">Intro template:</span>
                      <span className="world-db-summary-value" style={{ color: '#4f46e5', fontWeight: 600 }}>
                        {customTemplates.find(t => t.id === importTemplate)?.topic || importTemplate}
                      </span>
                    </div>
                    {Object.entries(importTemplateStages).map(([stage, tplId]) => {
                      if (!tplId) return null;
                      const stageLabels = { follow_up_1: 'Follow Up 1', follow_up_2: 'Follow Up 2', follow_up_3: 'Follow Up 3' };
                      return (
                        <div key={stage} className="world-db-summary-row import-option">
                          <span className="world-db-summary-label">{stageLabels[stage]} template:</span>
                          <span className="world-db-summary-value" style={{ color: '#4f46e5', fontWeight: 600 }}>
                            {customTemplates.find(t => t.id === tplId)?.topic || tplId}
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              
              {totalImportCount > 0 && duplicateCount > 0 && (
                <div className="world-db-duplicate-hint">
                  <p>
                    <strong>Note:</strong> If you've recently imported the same data and still see entries to import, 
                    there might be slight differences in the format of email addresses or business names. 
                    Check the console for detailed information.
                  </p>
                </div>
              )}
            </div>

            <div className="world-db-uploader-actions">
              <button 
                className="world-db-uploader-back-btn" 
                onClick={goBackToMapping}
                disabled={isUploading}
              >
                Back to Mapping
              </button>
              <button
                className={`world-db-uploader-submit-btn ${
                  (totalImportCount > 0 || enrichmentCount > 0) ? 'active' : 'inactive'
                } ${isUploading ? 'loading' : ''}`}
                onClick={confirmAndImport}
                disabled={(totalImportCount === 0 && enrichmentCount === 0) || isUploading}
              >
                {isUploading
                  ? 'Importing data...'
                  : `Import Data (${totalImportCount}${enrichmentCount > 0 ? ` + ${enrichmentCount} enriched` : ''})`}
              </button>
              
            </div>
                  
        {isUploading && (
          <div className="import-progress-bar-container">
            <div className="import-progress-bar" style={{ width: `${importProgress}%` }} />
            <div className="import-progress-label">{importProgress}%</div>
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldDbUploader;
